// ─────────────────────────────────────────────────────────────────────────────
// firebase/models.ts – Kanonische TypeScript-Interfaces & Enums (Single Source)
//
// Diese Datei ist die KANONISCHE Quelle fuer die Datenmodell-Typen und die
// Status-Aufzaehlungen (REQ-DM-030, REQ-DM-031, REQ-DM-040..042, REQ-DM-060).
// Sie wird vom Dashboard verwendet und dient zugleich als Referenz fuer die
// Cloud Functions (dort liegt eine eigene Kopie).
//
// Wichtig (REQ-OV-002): Statuswerte werden in Firestore technisch-englisch
// gefuehrt (`pending`, `printing`, ...). Die Uebersetzung passiert AUSSCHLIESSLICH
// in der Oberflaeche ueber vue-i18n ($t('status.*')), nie durch deutsche
// Statuswerte in der Datenbank.
//
// Kopplung Printer-Typ (REQ-DM-060):
//   Der REICHHALTIGE, vollstaendige `Printer`-Typ (mit allen Betriebsfeldern:
//   Name, Modelltyp, IP, Abholscreen-/Kitchen-TV-/Alarm-Einstellungen etc.) lebt
//   in `dashboard/src/stores/printerStore.ts`. Der hier definierte
//   `PrinterStub` ist bewusst nur ein kanonischer Stummel/Referenz fuer die
//   Cloud Functions. Bei jeder Datenmodell-Aenderung an Druckern muessen BEIDE
//   Stellen konsistent mitgezogen werden.
// ─────────────────────────────────────────────────────────────────────────────

import type { Timestamp } from 'firebase/firestore';

// ── Enums ──────────────────────────────────────────────────────────────────────

export enum RegisterStatus {
  ONLINE  = 'online',
  OFFLINE = 'offline',
}

/**
 * Zustellstatus eines Auftrags (REQ-DM-040): «Hat der Auftrag sein Ziel erreicht?»
 * Kanonische, abschliessende Aufzaehlung. Uebergaenge:
 *   pending → printing → completed | failed
 *   pending → expired (Verfall aus `pending`, siehe Lebenszyklus)
 *   printing → pending (Wiederherstellung haengender Auftraege)
 *
 * HINWEIS: Ein frueher existierender «Abhol»-Zustellstatus wurde entfernt.
 * Die «Abholung» ist ein Bearbeitungsstatus (siehe WorkStatus.DONE), keine
 * Zustelldimension.
 */
export enum PrintJobStatus {
  PENDING   = 'pending',
  PRINTING  = 'printing',
  COMPLETED = 'completed',
  FAILED    = 'failed',
  EXPIRED   = 'expired',
}

/**
 * Bearbeitungsstatus eines Auftrags (REQ-DM-041): «Wie weit ist die Bestellung
 * im Betrieb?». Nur fuer Bestellungen/Arbeitsbons.
 *
 * Orthogonal zum Zustellstatus (REQ-DM-042): Kassenbons tragen KEINEN `workStatus`.
 * UI-Labels (via vue-i18n): Offen / In Bearbeitung / Abholbereit / Erledigt.
 */
export enum WorkStatus {
  OPEN           = 'open',
  IN_PREPARATION = 'inPreparation',
  READY          = 'ready',
  DONE           = 'done',
}

/**
 * Ausgabeart eines Druckers/Kanals (REQ-DM-031).
 * `print`     = physischer ESC/POS-Drucker (mit Adresse + Modelltyp).
 * `kitchenTv` = Bildschirm-Kanal (kein Druckserver, keine Adresse).
 */
export enum PrinterOutputType {
  PRINT      = 'print',
  KITCHEN_TV = 'kitchenTv',
}

/** Modelltyp physischer Drucker (nur bei outputType `print`). */
export type PrinterModelType = 'Standard' | 'U220' | 'Sunmi';

// ── Druckerzustand: grob + fein (REQ-PS-020a) ─────────────────────────────────
// Der Zustand liegt auf ZWEI Feldern: das GROBE `status` bleibt beim
// entschiedenen Vokabular `ready` | `error` | `offline` (OP-15), die FEINERE,
// modellabhaengige Tiefe steht additiv in der Liste `statusDetail`. Erzeuger ist
// ausschliesslich der Druckserver (print-server/src/printer-status.js
// `deriveState` / `DETAIL`); die Werte bleiben technisch-englisch, die
// Uebersetzung erfolgt nur in der Oberflaeche (vue-i18n).

/**
 * Detailkennungen in stabiler ANZEIGE-Reihenfolge, deckungsgleich mit
 * `DETAIL_ORDER` in print-server/src/printer-status.js: schwerwiegendste Ursache
 * zuerst, `paperLow` zuletzt (die einzige Kennung, die fuer sich allein KEIN
 * Fehler ist und `status` auf `ready` belaesst).
 *
 * `notResponding` (siebte Kennung, T-M42.2 / REQ-PS-020a Praez. 1 bis 3): das
 * Geraet ist ERREICHBAR, hat zuvor geantwortet und bleibt drei Abfragerunden in
 * Folge stumm. Das grobe `status` daneben ist `error` (Praez. 3 (a)); eine
 * eigene Alarmstufe gibt es ausdruecklich NICHT — `PRINTER_ALARM_STAGES` bleibt
 * unveraendert, der Wechsel alarmiert ueber die bestehende Stufe `error`.
 */
export const PRINTER_STATUS_DETAILS = [
  'offline',
  'coverOpen',
  'paperOut',
  'cutterError',
  'error',
  'notResponding',
  'paperLow',
] as const;
export type PrinterStatusDetail = (typeof PRINTER_STATUS_DETAILS)[number];

/** Quelle des zuletzt geschriebenen Zustands (REQ-PS-010 / 011 / 012). */
export const PRINTER_STATUS_SOURCES = ['asb', 'poll', 'jobResult', 'connection'] as const;
export type PrinterStatusSource = (typeof PRINTER_STATUS_SOURCES)[number];

/** Modellabhaengige Detailtiefe (REQ-PS-001): Standard `full`, U220 `basic`, Sunmi `none`. */
export const PRINTER_STATUS_DETAIL_LEVELS = ['full', 'basic', 'none'] as const;
export type PrinterStatusDetailLevel = (typeof PRINTER_STATUS_DETAIL_LEVELS)[number];

/**
 * Alarmierbare Stufen je Drucker (REQ-PS-023 / REQ-PS-023a / REQ-PS-023c). Jede
 * angebotene Stufe MUSS durch einen tatsaechlich vom Druckserver geschriebenen
 * Zustand ausloesbar sein: `offline`/`error` aus dem Wechsel des groben
 * `status`, `paperLow`/`coverOpen`/`paperOut` aus dem Wechsel der Kennungen in
 * `statusDetail`.
 *
 * `paperOut` (T-M42.3, REQ-PS-023c) steht direkt nach `coverOpen` — dieselbe
 * Reihenfolge wie `ALARM_STAGES` in firebase/functions/src/alarms.ts. Sie ist
 * NICHT Teil des Standards (der bleibt `['offline']`, REQ-PS-023) und wird im
 * Formular nie von selbst angehakt (REQ-PS-023a Praez. 1 (b)).
 *
 * Ausloesbar heisst: an MINDESTENS EINEM Modelltyp. An welchem Modelltyp eine
 * Stufe nie ausloesen kann, fuehrt `utils/alarmStufenWirksamkeit.ts`
 * (REQ-PS-023a Praez. 3); die Oberflaeche kennzeichnet sie dort, sperrt sie
 * aber nicht.
 */
export const PRINTER_ALARM_STAGES = ['offline', 'error', 'paperLow', 'coverOpen', 'paperOut'] as const;
export type PrinterAlarmStage = (typeof PRINTER_ALARM_STAGES)[number];

/**
 * DIE DREI BILDSCHIRM-MODI (REQ-DM-068 (1), REQ-SC-104 (1); T-M25.2).
 *
 * Technisch-englisch in der Datenbank, uebersetzt ausschliesslich in der
 * Oberflaeche (REQ-OV-002, REQ-SC-106 (2)). Die Menge ist abschliessend — ein
 * vierter Wert ist ein Entscheid und kein Nebeneffekt.
 */
export const SCREEN_MODES = ['normal', 'light', 'fastline'] as const;
export type ScreenMode = (typeof SCREEN_MODES)[number];

/**
 * Herkunftskennzeichen eines Auftrags (REQ-DM-030, REQ-DM-033).
 * `ordermood` markiert Auftraege aus dem OrderMood-Kiosk-Eingang (Fastline ist
 * eine Teilmenge davon). Der `payfixwebhook` leitet diesen Wert ab, sobald
 * `isFromOrderMood || isFromOrderMoodTable` gilt.
 */
export type PrintJobSource = 'dashboard' | 'payfix_webhook' | 'ordermood';

// ── Company ───────────────────────────────────────────────────────────────────

export interface Company {
  id:        string;
  name:      string;
  address:   string;
  phone?:    string;
  email?:    string;
  /** Schweizer Unternehmens-Identifikationsnummer (UID, z. B. «CHE-123.456.789»). */
  uid?:      string;
  /** Freitext-Beschreibung des Mandanten (nur Anzeige im Dashboard). */
  description?:   string;
  /**
   * Logo des Mandanten als Data-URL (REQ-DM-010a Praez. 1 (1)); gedruckt oben
   * auf dem Kundenbeleg und dem Stornobeleg, auf der Zwischenrechnung nach
   * `interimReceiptLogo`, nie auf Abschluessen und nie am TM-U220
   * (REQ-PR-039, REQ-PR-034 Praez. 6 (b)).
   */
  logoUrl?:       string;
  /**
   * Schlusstext am Ende des Kundenbelegs, auf der Zwischenrechnung nach
   * `interimReceiptFooter` (REQ-PR-034 Praez. 6 (b)).
   */
  receiptFooter?: string;
  /**
   * Inhalt des Fuss-QR am Ende des Kundenbelegs, auf der Zwischenrechnung nach
   * `interimReceiptQr` (REQ-DM-010a, REQ-PR-034 Praez. 4 und 6 (b)). NICHT der
   * Bon-QR des Arbeitsbons — jener kodiert die Belegnummer und wird je Drucker
   * geschaltet (REQ-DM-031, REQ-SC-094).
   *
   * *Bis zum 2026-10-30 stand hier „Ziel-URL fuer den Bon-QR-Druck
   * (REQ-DM-031, Ausgabeart `print`)" und an `logoUrl` „erscheint in der
   * Oberflaeche" — berichtigt statt geloescht (REQ-DM-010a Praez. 2 (5),
   * REQ-OPS-036 Praez. 6).*
   */
  qrUrl?:         string;
  // ── Schalter der Zwischenrechnung (REQ-DM-010a Praez. 2) ────────────────────
  // Optional, Typ boolean. Fehlt ein Feld oder traegt es keinen booleschen
  // Wert, gilt die Vorgabe: Logo ja, Schlusstext nein, Fuss-QR nein
  // (REQ-PR-034 Praez. 6 (d)). Die Oberflaeche schreibt immer alle drei als
  // `true`/`false` (Praez. 2 (3)); die Regel des Lesens liegt allein in
  // `@/utils/zwischenrechnungSchalter`.
  /** Logo auf der Zwischenrechnung drucken. Vorgabe: ja. */
  interimReceiptLogo?:   boolean;
  /** Schlusstext (`receiptFooter`) auf der Zwischenrechnung drucken. Vorgabe: nein. */
  interimReceiptFooter?: boolean;
  /** Fuss-QR (`qrUrl`) auf der Zwischenrechnung drucken. Vorgabe: nein. */
  interimReceiptQr?:     boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // ── Launcher-/Update-Steuerung (REQ-VK-020) ──────────────────────────────────
  // Kanal der Geraetesoftware je Mandant. Standard (fehlend) = 'stable'. Ein
  // optionales `updateChannel` am Standort uebersteuert diesen Wert (REQ-VK-021);
  // ein `pinnedVersion` am Standort haelt ihn auf einer exakten Version fest
  // (REQ-VK-022). Verarbeitet von der Cloud Function `getReleaseForDevice` (M6).
  updateChannel?:         'stable' | 'beta';
  // Alarmzentrale
  alarmEmail?:            boolean;
  alarmSms?:              boolean;
  alarmOnQueueOffline?:   boolean;
  alarmOnJobTimeout?:     boolean;
  alarmOnPrinterOffline?: boolean;
  alarmEmails?:           string[];
  alarmPhones?:           string[];
}

// ── Location ────────────────────────────────────────────────────────────────────
// Standort eines Mandanten (REQ-DM-011). Ein Standort buendelt Kassen, Druckserver
// und Drucker. Der Zustand «Standort offline» (REQ-RS-042) wird NICHT persistiert,
// sondern rein clientseitig aus dem Status der zugehoerigen Druckserver abgeleitet
// (siehe stores/locationStore.ts, isLocationOffline). Es gibt daher KEIN
// `status`-Feld an dieser Stelle.

export interface Location {
  id:        string;
  companyId: string;
  name:      string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // ── Launcher-/Update-Steuerung (REQ-DM-013) ──────────────────────────────────
  // ACHTUNG: reine optionale Typfelder als kanonische Referenz. Die zugehoerige
  // Logik (Auto-Update, Verteilkanal, Version-Pinning) gehoert inhaltlich zu M6
  // und wird hier bewusst NICHT verarbeitet.
  updatePaused?:   boolean;
  updateChannel?:  'stable' | 'beta';
  pinnedVersion?:  string | null;
}

// ── Register ──────────────────────────────────────────────────────────────────
// Represents one Raspberry Pi / POS client.

export interface Register {
  id:        string;
  companyId: string;
  name:      string;
  /** Current connectivity status updated by the Pi's heartbeat. */
  status:    RegisterStatus;
  /** Server timestamp of the last heartbeat ping received. */
  lastPing:  Timestamp | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ── Printer (Stub / Referenz) ───────────────────────────────────────────────────
// ACHTUNG: Der vollstaendige, reichhaltige `Printer`-Typ lebt in
// `stores/printerStore.ts` (REQ-DM-060). Der folgende `PrinterStub` bildet nur die
// per Spec verbindlichen Schluesselfelder (REQ-DM-031) ab und dient als kanonische
// Referenz fuer die Cloud Functions. Beide Stellen bei Aenderungen mitziehen.

// HINWEIS zu Feldnamen: Die kanonischen, TATSAECHLICH persistierten Feldnamen
// werden hier 1:1 mit dem reichhaltigen Printer-Typ (stores/printerStore.ts)
// gehalten (REQ-DM-060).
//   - Modelltyp = `modelType` (VERTRAG mit Backend `setprinters` und dem Daemon-
//     Rendering print-server/src/printer.js). Niemals in `type` umbenennen, sonst
//     liest der Daemon `undefined` und faellt auf den Standard-Druckpfad zurueck
//     (TM-U220-Rotdruck / Sunmi-Puffermodus wuerden ausfallen).
//   - Bon-QR-Druck = `qrCode`, Anzeigedauer = `abholDisplaySec`. `qrCode` ist
//     bewusst frontend-intern (aktuell KEIN Backend-/Daemon-Leser); die Funktion
//     `completereadyorders` liest `abholDisplaySec` UND die Abschaltung
//     `abholAutoCompleteDisabled` (REQ-SC-080/REQ-SC-081, T-M12.20) — beide aus
//     demselben Nachschlag des `printers`-Dokuments.
//
// ── Strukturelle Ausschluss-Garantie der Ausgabeart (REQ-SC-004) ────────────────
// `PrinterStub` ist eine DISKRIMINIERTE UNION ueber `outputType`: ein Kanal ist
// ENTWEDER `print` (physischer Drucker, mit optionalem Bon-QR-Druck `qrCode`) ODER
// `kitchenTv` (Bildschirm-Kanal, OHNE jegliche print-Felder). Die Bon-QR-Druck-
// Option `qrCode` ist STRUKTURELL nur am print-Zweig vorhanden – ein kitchenTv-
// Kanal kann sie typseitig gar nicht tragen (REQ-SC-004: strukturell garantiert,
// nicht als Laufzeitregel). Der backend-seitige Sanitizer
// (firebase/functions/src/printerOutputExclusion.ts) kodiert dieselbe Regel zur
// Laufzeit (Verteidigung in der Tiefe).
//
// ── DIE ZUGESCHALTETE ANSICHT LOEST DIE UNION NICHT AUF (REQ-SC-084) ───────────
// Seit dem Entscheid zu OP-114 (Weg (c), 2026-08-07) kann ein Kanal der
// Ausgabeart `print` die Kitchen-TV-ANSICHT zuschalten (`kitchenTvEnabled`).
// Die AUSGABEART bleibt davon unberuehrt und die Union bleibt an `outputType`
// diskriminiert: die Ausgabeart sagt, wohin ZUGESTELLT wird, die zugeschalteten
// Ansichten sagen, wo etwas ZU SEHEN ist. `kitchenTvEnabled` steht deshalb
// ausschliesslich am `print`-Zweig — an `kitchenTv` laeuft die Ansicht immer und
// das Feld waere ohne Gegenstand (REQ-SC-084 (b)). Der Ausschluss gegen den
// Bon-QR-Druck (REQ-SC-004) gilt ab jetzt genau dieser Kombination:
// `qrCode === true` und `kitchenTvEnabled === true` nie zugleich.
//
// ── Zwei QR-Konzepte, strikt getrennt (REQ-SC-030 / REQ-SC-031) ────────────────
//   • Bon-QR-Druck  – AUSGABE auf Papier. Konfig-Feld `qrCode`, NUR am print-Zweig.
//   • QR-Abhol-Scan – EINGABE am Bildschirm. Sucht ueber die Belegnummer
//                     (`receiptNumber`) und vermerkt `scannedAt` am `PrintJob`
//                     (siehe unten). Der eingelesene Code stammt IMMER von aussen,
//                     NIE vom eigenen Bon-QR-Druck (REQ-SC-031).
//   Die beiden Begriffe werden nie gemeinsam oder mehrdeutig benannt.

/** Gemeinsame Felder beider Ausgabearten (Basis der diskriminierten Union). */
export interface PrinterStubBase {
  id:            string;
  companyId:     string;
  locationId?:   string;
  /** Ausgabeart: Diskriminator der Ausschluss-Struktur (REQ-DM-031 / REQ-SC-004). */
  outputType:    PrinterOutputType;
  // ── Drucker-Kennung der Kasse (REQ-DM-035 / REQ-DM-036) ─────────────────────
  /**
   * ZWECK: Aufloesungsschluessel des Alt-Eingangs. Die Kasse sendet ihre im
   * Payfix-Portal konfigurierte Kennung als `printer_id` im Alt-Umschlag;
   * `resolveLegacyPrinter` (firebase/functions/src/index.ts) sucht damit in
   * `printers` den Gleichheitstreffer auf `payfixPrinterId`. Es ist der EINZIGE
   * Suchpfad — ein Drucker ohne dieses Feld existiert aus Sicht der Kasse nicht
   * und quittiert den echten Bon mit HTTP 404 (Produktivstoerung 2026-08-10).
   *
   * DESHALB PFLICHT an JEDEM Provisionierungsweg (REQ-DM-035): REST-API
   * `POST /v1/printers` (400 bei Fehlen, 409 bei Dublette) UND die
   * Verwaltungsoberflaeche (REQ-DM-036 — ein Eingabefeld, gespiegelt auf das
   * Grundgeruest-Feld `number`, Eindeutigkeitspruefung vor dem Speichern).
   *
   * DESHALB ZEICHENKETTE: Firestore vergleicht typstreng; ein Zahlwert 3324
   * wird von der Abfrage nach '3324' nie gefunden — ohne Fehler, nur mit leerem
   * Ergebnis (index.ts:1458-1464). Der Alt-Eingang normalisiert seinerseits mit
   * `String(...)`, weil die Alt-Loesung Integer sendet.
   *
   * Systemweit eindeutig, an JEDER Ausgabeart — auch am Bildschirm-Kanal, weil
   * die Kasse ihn wie einen Drucker adressiert (REQ-SC-001). Optional
   * deklariert, damit Bestandsdokumente vor dem Nachtrag (REQ-OPS-040) lesbar
   * bleiben; die Anzeige liest `payfixPrinterId ?? number`
   * (dashboard/src/utils/payfixPrinterId.ts).
   */
  payfixPrinterId?: string;
  // ── Anzeige-Schluessel des Bildschirm-Kanals (REQ-SC-050, REQ-SEC-004) ───────
  /**
   * SHA-256-Hash des Anzeige-Schluessels (Feldvertrag T-M5.3). Der Klartext-
   * Anzeige-Schluessel wird – wie bei apiTokens – NIE persistiert; hier liegt
   * ausschliesslich sein Hash. Die Cloud Function `screentoken` vergleicht den
   * eingereichten Klartext-Schluessel gegen diesen Hash und stellt bei Treffer ein
   * eingeschraenktes Firebase-Custom-Token mit Anspruch genau auf diesen Kanal aus
   * (Claims screen/screenPrinterId/companyId); die Firestore-Rules pruefen additiv
   * gegen diesen Anspruch (REQ-SC-051). Ein Kanal ohne `screenKeyHash` kann kein
   * Screen-Token beziehen. Backend-verwaltetes Feld: Der reiche Printer-Typ in
   * `stores/printerStore.ts` traegt es als Konfig-Kennzeichen mit, sobald die
   * Setz-/Rotier-Oberflaeche entsteht (REQ-DM-060, dann konsistent nachziehen).
   */
  screenKeyHash?: string;
  /**
   * Schluesselpflicht des Bildschirm-Kanals (REQ-DM-063, REQ-SEC-024).
   *
   * VORZEICHENREGEL (i.d.F. 2026-08-24, REQ-DM-063 (2)): **Schluesselpflicht
   * besteht NUR bei ausdruecklich geschriebenem `screenKeyRequired === true`.**
   * Fehlendes Feld, `false` und jeder Fremdtyp bedeuten „kein Schluessel
   * erforderlich"; `screentoken` stellt das Anzeige-Token dann OHNE
   * Schluesselpruefung aus — mit denselben Claims und derselben companyId-
   * Pflicht. Gelesen wird `=== true` und nie eine blosse Wahrheitsprobe.
   * **Beide Werte werden ausdruecklich geschrieben**, nie durch Weglassen
   * (REQ-DM-063 (3)). `screenKeyHash` bleibt unberuehrt und wird NICHT
   * geloescht: wer die Pflicht wieder einschaltet, hat wieder denselben
   * Schluessel.
   *
   * ⚠ BIS ZUM 2026-08-24 STAND HIER DAS UMGEKEHRTE VORZEICHEN — berichtigt
   * statt geloescht (REQ-OPS-036 Praez. 6): „das FEHLEN des Feldes bedeutet
   * «Schluessel erforderlich». Nur der ausdrueckliche Wert `false` lockert;
   * `true`, ein Fremdtyp und das fehlende Feld bedeuten unveraendert
   * Schluesselzwang. So behaelt jeder Bestandskanal seinen Anzeige-Schluessel,
   * ohne dass ein einziges Dokument angefasst wird." **Aufgehoben durch den
   * Entscheid des Auftraggebers vom 2026-08-24** (REQ-SEC-024 Praez. 3 (o)/(p);
   * Umsetzung T-M26.3 Cloud, T-M26.4 Portal, T-M26.5 Bestand).
   */
  screenKeyRequired?: boolean;
  // ── Abholscreen-Einstellungen (REQ-SC-003, bei BEIDEN Ausgabearten) ──────────
  abholscreenEnabled?:     boolean;   // aktiv
  /**
   * Anzeigedauer abholbereiter Bestellungen in Sekunden (REQ-SC-003).
   *
   * GUELTIGER BEREICH 5 bis 600, Standard 60 (REQ-SC-080). Die Oberflaeche
   * begrenzt die Eingabe darauf; `completereadyorders` behandelt einen in der
   * Datenbank vorgefundenen Wert AUSSERHALB des Bereichs wie einen fehlenden,
   * also als Standard 60 (Auslegung in
   * `firebase/functions/src/abholDisplay.ts`, im Dashboard gespiegelt in
   * `utils/abholDisplayDuration.ts`). Nachfolger des Alt-Feldes
   * `queue_display_duration`.
   */
  abholDisplaySec?:        number;
  /**
   * DIE ABSCHALTUNG des selbsttaetigen Erledigens (REQ-SC-081 samt
   * Praezisierung 1, T-M12.20). Ist sie gesetzt, laesst `completereadyorders`
   * die abholbereiten Bestellungen dieses Kanals unberuehrt — sie bleiben
   * `ready` bis zum Abschluss von Hand.
   *
   * ⚠ NEGATIV GEPOLT, UND DAS IST ERZWUNGEN, NICHT GEWAEHLT. Nach der
   * Auslegungsregel bedeuten ein fehlendes Feld und jeder Fremdtyp „nicht
   * gesetzt" (REQ-SC-084 (c)). Traege das Feld die positive Bedeutung
   * („selbsttaetiges Erledigen eingeschaltet"), hiesse „Feld fehlt"
   * ABGESCHALTET — und kein einziger bestehender Kanal traegt es;
   * `completereadyorders` laeuft ueber ALLE Kanaele und liesse damit die
   * abholbereiten Bestellungen der ganzen Flotte stehen, bis
   * `completestaleorders` sie vier Stunden spaeter abraeumt.
   *
   * EIGENER WAHRHEITSWERT und ausdruecklich NICHT der Wert `0` in
   * `abholDisplaySec`: eine Null waere von einem fehlenden Feld und von einem
   * Eingabefehler nicht zu unterscheiden, und der eingestellte Zeitwert soll
   * beim Wiedereinschalten unveraendert weiterwirken.
   *
   * GRENZE, die die Beschriftung in der Oberflaeche ausdruecklich nennt: die
   * Vier-Stunden-Bereinigung `completestaleorders` (REQ-SC-015) bleibt
   * unberuehrt (OP-116, Weg (a)). „Abschaltbar" heisst „kein selbsttaetiges
   * Erledigen nach der Anzeigedauer", nicht „steht ewig".
   *
   * KEIN ALT-NAME: die Alt-Loesung kennt die Abschaltung nicht (sie fuehrt
   * allein `queue_display_duration`); der Name ist in REQ-SC-081 Praez. 1
   * festgelegt und woertlich verbindlich. Der reiche `Printer`-Typ in
   * `stores/printerStore.ts` fuehrt ihn ebenso (REQ-DM-060).
   */
  abholAutoCompleteDisabled?: boolean;
  abholscreenMaxItems?:    number;    // Obergrenze gleichzeitig angezeigter Nummern
  /**
   * DIE TISCHBEREICHE DIESES AUSGABEKANALS (REQ-DM-073) — eine Liste von
   * Bereichen mit EINSCHLIESSLICHEN Grenzen.
   *
   * FORM UND FELDNAMEN (REQ-DM-073 (a)): genau zwei Zahlfelder `von` und `bis`,
   * beide `number`, beide einschliesslich. **Die Feldnamen sind deutsch und
   * bleiben es** — sie stehen seit dem Bestand so in den Dokumenten der
   * Produktion und fallen damit unter REQ-OV-008 (ein Umbenennen waere eine
   * Datenwanderung ueber 3117 Drucker). Es sind KEINE Statuswerte; REQ-OV-002
   * ist nicht beruehrt.
   *
   * IN `PrinterStubBase` UND NICHT IN EINEM ZWEIG: das Feld gilt an BEIDEN
   * Ausgabearten (REQ-DM-073 (d)) — die Kasse adressiert einen Bildschirm-Kanal
   * wie einen Drucker (REQ-SC-001), und der Bereich wirkt seit dem Entscheid vom
   * 2026-09-17 (Payfix/Tim Schuetz) ausdruecklich AUCH auf Kitchen TV,
   * Abholscreen und Fastline (REQ-LC-048 (i)).
   *
   * AUSLEGUNG (REQ-DM-073 (c), REQ-LC-048 (b)/(f)): ein Eintrag zaehlt nur,
   * wenn `von` UND `bis` endliche Zahlen sind und `von <= bis`; jeder andere
   * Eintrag wird STILL UEBERGANGEN (fehlend, `null`, Zeichenkette — auch `'2'`,
   * `NaN`, vertauschte Grenzen). Fehlendes Feld, leere Liste und eine Liste ohne
   * einen gueltigen Eintrag bedeuten dasselbe: KEIN Filter. Mehrere Bereiche
   * stehen in ODER-Verknuepfung, die Grenzen zaehlen mit. Die Richtung dieser
   * Strenge ist verlustscheu und zeigt immer auf „anlegen".
   *
   * ⚠ DAS FELD HAT SEIT M43 EINEN LESER (REQ-DM-073 (e)): der Auftragseingang
   * `ingestPrintJob` uebergeht einen ARBEITSBON, dessen Tischnummer ausserhalb
   * aller Bereiche liegt — kein `printJobs`-Dokument, Antwort 200 mit
   * `skipped: true` und `reason: 'tableRangeMismatch'` (REQ-LC-048). Ein
   * Kassenbon wird NIE gefiltert (REQ-LC-048 (c)), ein Bon ohne lesbare
   * Tischnummer wird IMMER angelegt (REQ-LC-048 (e)). Gelesen wird das Feld im
   * SELBEN Lesevorgang des `printers`-Dokuments wie `outputType` — kein
   * zusaetzlicher Firestore-Zugriff im heissen Pfad.
   *
   * ⚠ DIE REST-API TRAEGT DAS FELD DAUERHAFT NICHT (Entscheid des Auftraggebers
   * vom 2026-09-17, OP-224 geschlossen): Tischbereiche sind Printerque-only,
   * sie werden ausschliesslich in der Verwaltungsoberflaeche gepflegt und
   * kommen nie von Payfix.
   *
   * Geschrieben wird es an genau einer Stelle: `PrinterDetailView.save()`
   * (unbedingt, auch als `[]`, und nur mit vollstaendigen Eintraegen). Der
   * reiche `Printer`-Typ in `stores/printerStore.ts` fuehrt dasselbe Feld in
   * derselben Form (REQ-DM-060, REQ-DM-073 (g)).
   */
  tableRanges?:            Array<{ von: number; bis: number }>;
  /**
   * FAST LINE — ein MODUS DES ABHOLSCREENS (REQ-SC-085), deshalb steht das Feld
   * hier neben `abholscreenEnabled` und nicht beim Kitchen TV. Ist es gesetzt,
   * laeuft der Abholscreen dieses Kanals im Fastline-Modus: die Gastsicht weicht
   * der Ausgabesicht mit Scan und Positionsliste.
   *
   * IN `PrinterStubBase` UND NICHT IN EINEM ZWEIG: die Einstellung gilt an jedem
   * Kanal mit zugeschaltetem Abholscreen und damit an BEIDEN Ausgabearten
   * (REQ-SC-085 (b)). Im `kitchenTv`-Zweig abgelegt waere sie am zugeschalteten
   * `print`-Kanal nicht tragbar.
   *
   * DER FELDNAME IST FEST (OP-119 Weg (a), REQ-SC-085 (c)); das Praefix ist eine
   * historische Namensform aus dem Alt-Bestand und behauptet KEINE Zugehoerigkeit
   * zum Kitchen TV. Das Repo fuehrte bis zum 2026-08-11 `fastline`.
   */
  kitchenTvFastLine?:      boolean;
  // ── Kitchen-TV-Einstellungen (REQ-SC-077, bei BEIDEN Ausgabearten) ───────────
  // Sie gelten an der Ausgabeart `kitchenTv` (dort laeuft die Ansicht immer,
  // REQ-SC-084 (b)) UND an einem `print`-Kanal mit `kitchenTvEnabled`. Deshalb
  // stehen sie in der Basis; die ZUSCHALTUNG `kitchenTvEnabled` selbst bleibt am
  // `print`-Zweig, weil sie nur dort einen Gegenstand hat. Nachgezogen mit
  // T-M12.18 (REQ-DM-060): der reiche Typ in `stores/printerStore.ts` fuehrte
  // sie, der kanonische Stummel bis dahin nicht.
  kitchenTvLight?:         boolean;   // Kachelansicht (REQ-SC-074)
  /**
   * DER AKTIVE BILDSCHIRM-MODUS DIESES KANALS (REQ-DM-068, REQ-SC-104 (1)).
   *
   * Genau EIN Modus ist an einem Kanal aktiv; dieses Feld nennt ihn. Geschrieben
   * wird es AUSSCHLIESSLICH serverseitig ueber die Cloud Function `kitchenaction`
   * (Aktion `setScreenMode`, REQ-SC-063, REQ-DM-068 (3)) — weder das Portal noch
   * eine Bildschirm-Ansicht schreibt es ueber das Web-SDK, und die
   * Firestore-Rules bleiben dafuer unveraendert (ein Anzeige-Token darf
   * `printers` nicht schreiben).
   *
   * ⚠ FEHLENDES FELD UND JEDER FREMDWERT BEDEUTEN „KEIN MODUS AKTIV"
   * (REQ-DM-068 (2)) — nicht `normal`. Es gibt KEINEN Vorgabewert, KEINE
   * Datenwanderung und ausdruecklich KEINE Ableitung aus `kitchenTvLight` oder
   * `kitchenTvFastLine`: die Altfelder sagen, was jemand einmal im Portal
   * eingestellt hat, dieses Feld sagt, welcher Bildschirm den Kanal gerade
   * uebernommen hat (REQ-SC-104 (5)). Die Altfelder bleiben am Dokument geduldet
   * und werden nicht geraeumt (REQ-SC-103 (4)).
   *
   * ⚠ KEIN INDEX (REQ-DM-068 (4)): das Feld wird gelesen, nie abgefragt — es
   * steht in keiner `where`- und in keiner `orderBy`-Bedingung. Wer je darauf
   * filtert, braucht zuerst einen Index (REQ-DM-050, REQ-OPS-039).
   *
   * Es steuert die ANSICHTSLOGIK und sonst nichts (REQ-DM-068 (6)); die
   * Ausgabeart bleibt der alleinige Traeger der Zustellung (REQ-SC-020/021/022).
   * BEIDE Printer-Typen fuehren das Feld mit demselben Wortlaut (REQ-DM-060,
   * REQ-DM-068 (7)): `stores/printerStore.ts` und `firebase/models.ts`.
   */
  activeScreenMode?:       ScreenMode;
  autoStartTimer?:         boolean;   // Artikel-Countdown startet selbsttaetig (REQ-SC-071)
  /**
   * DER DRUCK IST ABBESTELLT (REQ-SC-077, REQ-LC-043).
   *
   * Ist der Wert gesetzt und die Kitchen-TV-Ansicht zugeschaltet, druckt der
   * Kanal den ARBEITSBON nicht mehr: der Webhook legt ihn unmittelbar mit
   * `status: 'completed'`, `completedAt` und `deliveredWithoutPrint: true` an —
   * ohne `printedAt`, weil nie ein Geraet beteiligt war. Der KASSENBON an
   * demselben Kanal wird weiterhin gedruckt (REQ-LC-009 Abgrenzung 1); Papier
   * ist dort Pflicht.
   *
   * IN `PrinterStubBase` UND NICHT IM `print`-ZWEIG: REQ-SC-077 fuehrt die
   * Einstellung an BEIDEN Kanalarten. An der Ausgabeart `kitchenTv` bleibt sie
   * ohne Wirkung — dort wird ohnehin nie gedruckt (REQ-SC-020) —, und das ist
   * kein Verstoss gegen REQ-PS-023a: die Wirkung faellt dort mit dem
   * Bestehenden zusammen (REQ-SC-077 Praez. 1).
   *
   * AUSLEGUNG (REQ-SC-077 letzter Satz): fehlendes Feld und jeder Fremdtyp
   * bedeuten „nicht gesetzt" — der Webhook liest `=== true`. Der reiche
   * `Printer`-Typ in `stores/printerStore.ts` fuehrt das Feld ebenfalls
   * (REQ-DM-060).
   */
  disablePrintingWhenKitchenTv?: boolean;
  // ── Alarm-Einstellungen ─────────────────────────────────────────────────────
  alarmEnabled?:  boolean;                // Alarmierung aktiv
  alarmMuted?:    boolean;                // stummgeschaltet
  /**
   * Alarmierte Stufen (REQ-PS-023 / REQ-PS-023a / REQ-PS-023c). Alle fuenf
   * Stufen sind vom Druckserver ausloesbar — an mindestens einem Modelltyp:
   * `offline`/`error` aus dem Wechsel des groben `status`,
   * `paperLow`/`coverOpen`/`paperOut` aus dem Wechsel der Kennungen in
   * `statusDetail`. `resolveAlarmStages` in firebase/functions/src/alarms.ts
   * liest genau dieses Vokabular. *Bis zum 2026-09-25 stand hier „Alle vier
   * Stufen" — mit T-M42.3 kam `paperOut` hinzu.*
   */
  alarmStages?:   PrinterAlarmStage[];
  // ── Archivierung (REQ-SEC-005) ──────────────────────────────────────────────
  /**
   * Archiviert = deaktiviert. NUR `archived === true` gilt als archiviert;
   * fehlendes Feld und `false` sind aktiv. Der Webhook weist Auftraege an
   * archivierte Drucker im Validierungspfad ab (T-M7.7, REQ-LC-004), die
   * Oberflaeche bietet sie nicht mehr als Zielkanal an.
   */
  archived?:      boolean;
  archivedAt?:    Timestamp;
  // ── Zustand (vom Druckserver geschrieben, REQ-LC-042 / REQ-PS-020) ───────────
  /**
   * GROBER, technisch-englischer Betriebsstatus des Druckers. Der Print-Server
   * schreibt bei erfolgreichem Druck `ready` und im Fehlerfall `error`. Weitere
   * Werte (`online`, `offline`, `printing`) siehe `PrinterRuntimeStatus` im
   * reichen Printer-Typ (stores/printerStore.ts). Das Vokabular bleibt bei
   * `ready` | `error` | `offline` (OP-15) — die feinere Tiefe steht ADDITIV in
   * `statusDetail`, nicht hier. Uebersetzung nur im UI (vue-i18n).
   */
  status?:        string;
  /**
   * Feinere, modellabhaengige Detailtiefe als LISTE technisch-englischer
   * Kennungen (REQ-PS-020a). Geschrieben von `deriveState`
   * (print-server/src/printer-status.js), gelesen vom Dashboard (REQ-PS-021)
   * und von der Alarm-Stufenermittlung (REQ-PS-023a).
   */
  statusDetail?:      PrinterStatusDetail[];
  /** Welche der drei Quellen den Zustand gesetzt hat (REQ-PS-010/011/012). */
  statusSource?:      PrinterStatusSource;
  /** Detailtiefe, die das Modell technisch liefern kann (REQ-PS-001). */
  statusDetailLevel?: PrinterStatusDetailLevel;
  /** Zeitpunkt der letzten Zustandsschreibung durch den Druckserver. */
  statusUpdatedAt?:   Timestamp;
  /** Letzte Fehlermeldung des Druckservers (nur bei status `error`). */
  lastError?:     string;
}

/**
 * Physischer Drucker (Ausgabeart `print`). Traegt die print-only-Felder inkl. der
 * Bon-QR-Druck-Option `qrCode` (REQ-SC-004: NUR hier vorhanden).
 */
export interface PrintPrinterStub extends PrinterStubBase {
  outputType:     PrinterOutputType.PRINT;
  /** Zugeordneter Druckserver (nur Ausgabeart `print`). */
  printserverId?: string;
  /** Drucker-IP (nur Ausgabeart `print`). */
  ipAddress?:     string;
  /** Drucker-Port (nur Ausgabeart `print`). */
  port?:          number;
  /** Modelltyp (nur Ausgabeart `print`). Kanonisch `modelType` (Backend-/Daemon-Vertrag). */
  modelType?:     PrinterModelType;
  /**
   * Bon-QR-Druck (AUSGABE auf Papier, REQ-SC-004/030): QR mit der Bestell-Kennung
   * auf dem Bon. Strukturell NUR an der Ausgabeart `print`. Nicht zu verwechseln
   * mit dem QR-Abhol-Scan (EINGABE am Bildschirm -> `PrintJob.scannedAt`).
   *
   * AUSSCHLUSS (REQ-SC-004 in seiner Fassung vom 2026-08-07 / REQ-SC-084 (a)):
   * nie zusammen mit `kitchenTvEnabled === true` — siehe dort.
   */
  qrCode?:        boolean;
  /**
   * ZUSCHALTUNG DER KITCHEN-TV-ANSICHT (REQ-SC-084, REQ-DM-031, REQ-SC-077).
   *
   * Ein Kanal der Ausgabeart `print` kann die Kitchen-TV-Ansicht zuschalten und
   * bleibt dabei in JEDER ANDEREN HINSICHT ein vollwertiger physischer Drucker:
   * Druckserver, Adresse, Modelltyp, Zustellzweig und alle zeitgesteuerten
   * Funktionen sind unveraendert (Payfix: „ein Drucker mit aktiviertem Kitchen
   * TV bleibt ein vollwertiger physischer Drucker, der zusaetzlich als
   * Bildschirm sichtbar ist — kein Entweder-oder auf Objektebene").
   *
   * STRUKTURELL NUR AM `print`-ZWEIG (REQ-SC-084 (b)): an der Ausgabeart
   * `kitchenTv` laeuft die Ansicht immer; das Feld waere dort ohne Gegenstand
   * und wird weder angeboten noch ausgewertet. Deshalb steht es hier und nicht
   * in `PrinterStubBase` — die Union bleibt an `outputType` diskriminiert und
   * wird durch diese Zuschaltung NICHT aufgeloest (REQ-SC-001 Praez. 1).
   *
   * AUSSCHLUSS GEGEN DEN BON-QR-DRUCK (REQ-SC-084 (a) / REQ-SC-004): `qrCode`
   * und `kitchenTvEnabled` stehen an demselben Dokument nie zugleich auf `true`.
   * Der Ausschluss ist strukturell garantiert — Compile-time-Assert und
   * Laufzeit-Sanitizer in `firebase/functions/src/printerOutputExclusion.ts`,
   * gegenseitige Abschaltung in der Druckerverwaltung
   * (`dashboard/src/utils/kitchenTvSwitch.ts`).
   *
   * AUSLEGUNG (REQ-SC-084 (c)): fehlendes Feld und jeder Fremdtyp bedeuten
   * „nicht zugeschaltet". Die ~200 uebernommenen Kanaele tragen es nicht.
   *
   * Der reiche Printer-Typ in `stores/printerStore.ts` fuehrt dasselbe Feld
   * (REQ-DM-060) — beide bei Aenderungen mitziehen.
   */
  kitchenTvEnabled?: boolean;
}

/**
 * Bildschirm-Kanal (Ausgabeart `kitchenTv`). Traegt KEINE print-Felder und
 * insbesondere KEIN `qrCode` (REQ-SC-004: strukturell ausgeschlossen) und KEIN
 * `kitchenTvEnabled` (REQ-SC-084 (b): dort laeuft die Ansicht immer, das Feld
 * waere ohne Gegenstand und wird weder angeboten noch ausgewertet).
 */
export interface KitchenTvPrinterStub extends PrinterStubBase {
  outputType: PrinterOutputType.KITCHEN_TV;
}

/**
 * Kanonischer Stummel (Referenz fuer die Cloud Functions, REQ-DM-060) als
 * diskriminierte Union: ENTWEDER Drucker (mit optionalem Bon-QR-Druck) ODER
 * Kitchen TV, nie beides (REQ-SC-004).
 */
export type PrinterStub = PrintPrinterStub | KitchenTvPrinterStub;

// ── printerKeys: das Sperrdokument der `payfixPrinterId` ──────────────────────
//
// REQ-DM-036 Praez. 4 (viii) (die drei Felder, abschliessend), REQ-OV-008
// Praez. 2 (`printerKeys` und seine Feldnamen sind FESTE NAMEN), REQ-DM-035
// (die Kennung ist der einzige Aufloesungsschluessel des Alt-Eingangs),
// REQ-DM-060 (diese Datei ist die kanonische Typquelle). Angelegt am
// 2026-09-18 mit T-M41.8.
//
// ⚠ DER GEGENSTAND IST DIE DOKUMENT-ID, NICHT DER INHALT. `printerKeys/{id}`
// traegt als Dokument-Kennung die kanonisierte `payfixPrinterId` (`String(…)`
// + `trim()`), und DARIN liegt die systemweite Eindeutigkeit — sie gilt ohne
// jede Abfrage. Der Inhalt sagt nur, WEM die Kennung gehoert; er wird beim
// Aendern und Loeschen gelesen, damit kein Schreiber die Belegung eines
// Dritten freigibt.
//
// ⚠ DIE FELDMENGE IST ABSCHLIESSEND, UND DIE RULES SETZEN SIE DURCH
// (`hasOnly(['printerId','companyId','createdAt'])`). Ein viertes Feld wird
// abgewiesen, nicht ignoriert. **Es gibt kein `update`:** ein Kennungswechsel
// ist `delete`-alt + `create`-neu im selben Commit.
//
// VIER SCHREIBER, und deshalb steht der Typ hier: `POST /v1/printers`,
// `PUT /v1/printers/:id` (beide `firebase/functions/src/index.ts`), die
// Verwaltungsoberflaeche (`dashboard/src/services/printerKeyService.ts`) und
// das Wanderungsskript der Bestandswanderung (T-M41.11).

/**
 * Sperrdokument einer Payfix-Drucker-Kennung — `printerKeys/{payfixPrinterId}`.
 *
 * ⚠ **`companyId` KANN LEER SEIN.** Die Bestandswanderung laeuft ueber das
 * Admin SDK und damit an den Rules vorbei (REQ-OPS-040); ein Bestandsdrucker
 * ohne Mandanten (REQ-DM-036 Praez. 2) hinterlaesst dann ein Sperrdokument
 * ohne lesbaren Eigentuemer — die Oberflaeche nennt das eine **verwaiste
 * Kennung** und behauptet ausdruecklich keine Loeschung (Praez. 2 (ii)).
 */
export interface PrinterKey {
  /** Dokument-Kennung des belegenden Druckers (`printers/{id}`). */
  printerId: string;
  /** Mandant des belegenden Druckers. */
  companyId: string;
  /** Serverzeitstempel der Belegung. */
  createdAt: Timestamp;
}

// ── Payfix Payload ────────────────────────────────────────────────────────────
// Die rohen Belegnutzdaten der Payfix-Kasse.
//
// WICHTIG (REQ-LC-007): Printerque speichert diese Nutzdaten UNVERAENDERT und
// VOLLSTAENDIG — keine Feld-Allowlist, kein Verwerfen unbekannter Felder, keine
// Umformung. Die Typen hier sind deshalb KEIN Schema und werden zur Laufzeit
// nirgends erzwungen; sie beschreiben, was beobachtet wird. Verbindlich sind die
// Anforderung und ihr Nachweis, nicht die Enge dieser Deklarationen.

/**
 * Auftragsposition eines Arbeitsbons (`payload.items[]`) — und mit denselben
 * Feldern ihre Unterposition (`payload.items[].items[]`).
 *
 * Feldbestand aus der Fixture-Wahrheit
 * (`print-server/test/fixtures/golden/<modell>/arbeitsbon-<fall>/input.json`,
 * OP-60): die
 * frueher hier gefuehrten `name`/`quantity`/`unitPrice`/`total` waren der
 * Feldvertrag des mit T-M8.1 entfernten M1-Geruests und kommen real nirgends
 * vor. ALLE Felder sind optional, weil eine Unterposition regelmaessig nur eine
 * Teilmenge traegt (`ShowArticleSmall`, `articleName`, `getCountString`).
 *
 * Die Index-Signatur bildet REQ-LC-007 auf Positionsebene ab — wie bei
 * `PayfixPayload`: ein Printerque unbekanntes Zusatzfeld hat hier ausdruecklich
 * Platz, statt bloss zur Laufzeit geduldet zu werden.
 */
export interface PayfixItem {
  articleId?:        number;
  articleName?:      string;
  /** Menge als Zeichenkette, wie von der Kasse geliefert (z. B. "2"). */
  getCountString?:   string;
  price?:            number;
  comment?:          string;
  /**
   * Gang-Zuordnung; ohne Information gilt `0` = kein Gang.
   *
   * ⚠ DER TYP IST `number | string`, UND DAS IST DIE FIXTURE-WAHRHEIT, KEINE
   * VORSICHT (berichtigt am 2026-08-11 mit T-M12.41, OP-108): hier stand
   * `number`, waehrend `epson-standard/arbeitsbon-gaenge-umlaut` und
   * `sunmi/arbeitsbon-gaenge-umlaut` an Position 1 `"Ürli"` und an Position 0
   * die Zeichenkette `"1"` fuehren. Der Druckserver setzt den Wert woertlich in
   * die Gang-Kopfzeile („GANG Ürli", `work-receipt.js:251`).
   *
   * Die Auslegung fuer Bildschirm und Positionsschluessel liegt an genau einer
   * Stelle je Komponente (`gangSchluessel`, REQ-SC-064 Praez. 2): ohne
   * Information `'0'`, als Zahl lesbar die kanonische Zahlform, sonst der
   * getrimmte Text selbst. Der Wert selbst wird NICHT umgewandelt gespeichert
   * (REQ-LC-007); ob die Kasse kuenftig nur noch Zahlen sendet, ist als OP-108
   * offen und hier ausdruecklich NICHT entschieden.
   */
  gangId?:           number | string;
  isCancelled?:      boolean;
  /**
   * Payfix-Artikel-Setting „Einzelbon" (REQ-SC-066 Praez. 2, T-M49.2). Allein
   * das Literal `true` setzt die Einzelbon-Menge in Kraft; Typ `unknown`, weil
   * Fremdtypen Feldwahrheit sind und als „kein Einzelbon" gelten.
   */
  isSingleArticle?:  unknown;
  /**
   * Stueckzahl DIESES Auftrags. Beim Einzelbon (`isSingleArticle === true`)
   * ist sie die Menge der Hauptposition — `getCountString` traegt dort die
   * Gesamtstueckzahl der Kassenzeile (gemessen 2026-10-09: `count: "1"`,
   * `getCountString: "2"`). Ausgelegt allein von `einzelbonMenge`
   * (`utils/kitchenView.ts`); nie fuer den Positionsschluessel (REQ-SC-064
   * Praez. 1 (c)). Typ `unknown`: das Feld ist typinstabil (Zahl, Double,
   * Zeichenkette) und wird nur gelesen (REQ-LC-007).
   */
  count?:            unknown;
  /** Kennzeichnet die Position als klein zu setzende Unterposition. */
  ShowArticleSmall?: boolean;
  /** Unterpositionen (Optionen, Zusaetze) — dieselbe Struktur, eine Ebene tiefer. */
  items?:            PayfixItem[];
  /**
   * Produktionszeit in MINUTEN (REQ-DM-034), auf Position UND Unterposition.
   *
   * Kanonische Drahtform ist die ZEICHENKETTE (Payfix-Entscheid 2026-07-30:
   * „Zeichenkette (String), wie im Alt-System. Bitte nicht umwandeln.").
   * Printerque SPEICHERT den Wert nur durch und wandelt ihn NICHT um — weder in
   * eine Zahl noch zurueck.
   *
   * ⚠ BERICHTIGT AM 2026-08-11 (T-M12.6, REQ-SC-071): hier stand „die
   * Umrechnung fuer den Countdown am Kitchen TV liegt bei Payfix
   * (Leistungsabgrenzung Spec 50)". Das ist mit REQ-SC-071 ueberholt — DIE
   * AUSWERTUNG LIEGT BEI PRINTERQUE: die Kuechensicht rechnet aus diesem Wert
   * den Artikel-Countdown (`produktionsSekunden` und die uebrigen reinen
   * Funktionen in `utils/kitchenView.ts`, Abschnitt 7). Unveraendert bleibt der
   * Satz darueber: die Auswertung geschieht IN DER ANZEIGE, nicht am Datenpfad —
   * gespeichert wird der Wert weiterhin 1:1 (REQ-DM-034, REQ-LC-007).
   *
   * Der aufnehmende Typ bleibt bewusst tolerant (`string | number`): der enge
   * Typ waere eine reine TypeScript-Aussage ohne Laufzeitwirkung und zugleich
   * die Einladung, spaeter eine Normalisierung zu ergaenzen, die den Wert kappt
   * — genau der stille Verlust, gegen den REQ-LC-007 gebaut ist
   * (Begruendung in REQ-DM-034).
   *
   * NICHT zu verwechseln mit `abholDisplaySec` (REQ-SC-003, Sekunden, am
   * Drucker-Dokument statt an der Position).
   */
  product_time?:     string | number;
  /** Jedes weitere Feld der Kasse — unveraendert durchgereicht (REQ-LC-007). */
  [key: string]:     unknown;
}

/**
 * Tischangabe eines Arbeitsbons (`payload.restaurantTable`).
 *
 * Fixture-belegt in JEDER Arbeitsbon-Fixture als Objekt mit `number` und
 * `name` (beide Zeichenketten). Beide Felder sind optional, weil der Bestands-
 * Renderer sie einzeln prueft (`data.restaurantTable && data.restaurantTable.name
 * != undefined`, `print-server/src/render/work-receipt.js`) — er rechnet also
 * selbst mit einer unvollstaendigen Angabe.
 */
export interface PayfixRestaurantTable {
  /** Tischnummer als Zeichenkette (z. B. "2"). */
  number?: string;
  /** Tischbezeichnung (z. B. "Tisch 2"). */
  name?:   string;
  /** Jedes weitere Feld der Kasse — unveraendert durchgereicht (REQ-LC-007). */
  [key: string]: unknown;
}

/**
 * Belegnutzdaten eines Druckauftrags (`printJobs/{id}.payload`, REQ-DM-030).
 *
 * FELDBESTAND AUS DER FIXTURE-WAHRHEIT (T-M10.5, OP-74). Vorher standen hier
 * `company`/`PayfixCompany`, `cashier`, `subtotal`, `taxRate`, `tax`, `total`,
 * `paymentMethod`, `amountGiven`, `change`, `footer` und `timestamp` — der
 * Feldvertrag des mit T-M8.1 entfernten M1-Geruests, im System nirgends belegt.
 * Es ist derselbe Mangel, den T-M9.1 eine Ebene tiefer an `PayfixItem` behoben
 * hat. `PayfixCompany` ist ersatzlos entfallen: kein Beleg, kein Verwender.
 *
 * DER BELEG IST MASCHINELL GEFUEHRT, nicht hier behauptet —
 * `dashboard/test/payfixPayloadContract.test.ts` liest die Feldnamen aus dieser
 * Deklaration und verlangt fuer jede genau eine von zwei Belegarten:
 *   (A) FIXTURE — der Feldname ist Schluessel im `payload` mindestens einer
 *       Golden-Master-Fixture (`print-server/test/fixtures/golden/<modell>/
 *       <fall>/input.json`, Spec 25).
 *   (B) BESTAND — der `payfixwebhook` liest das Feld nachweislich aus dem
 *       Payload (`firebase/functions/src/index.ts`). Diese Klasse ist noetig,
 *       weil die Kassenbon-Fixtures den `epson_objects`-Inhalt als BLOSSES
 *       ARRAY fuehren (Renderer-Eingabevertrag, `print-server/src/printer.js`:
 *       `payload.epson_objects`) und weil Idempotenz- und Herkunftsfelder
 *       Cloud-Anliegen sind, die kein Beleg-Rendering beruehrt.
 * Ein Feld ohne Beleg laesst den Test rot umfallen.
 *
 * ALLE Felder sind optional: ein Kassenbon fuehrt `epson_objects` und keines
 * der Arbeitsbon-Felder, ein Arbeitsbon umgekehrt; `sunmi`,
 * `price_in_workreceipts`, `is_takeaway` und `id` kommen nur in einzelnen
 * Faellen vor.
 *
 * DIE INDEX-SIGNATUR BLEIBT (REQ-LC-007): der Webhook fuehrt keine
 * Feld-Allowlist und verwirft nichts — jedes weitere Feld der Kasse erreicht
 * unveraendert den Druckserver und die Bildschirm-Kanaele. Der Typ darf das
 * Gegenteil nicht suggerieren.
 */
export interface PayfixPayload {
  // ── Arbeitsbon-Kopf (Fixture-Beleg) ────────────────────────────────────────
  /** Belegdatum, wie von der Kasse geliefert (z. B. "22.11.2023"). */
  date?:            string;
  /** Belegzeit (z. B. "12:45:28"). */
  time?:            string;
  /** Geraetename der Kasse (z. B. "iPad_1234"). */
  device?:          string;
  /** Bediener (z. B. "Mitarbeiter Allgemein"). */
  employee?:        string;
  /** Anzahl Gaeste. */
  guest_number?:    number;
  /**
   * Belegnummer (REQ-DM-030). Der Webhook uebernimmt sie an das Auftrags-
   * dokument und wandelt dabei defensiv in eine Zeichenkette
   * (`String(...)`, `firebase/functions/src/index.ts`); die Fixtures fuehren
   * sie durchgehend als Zeichenkette.
   *
   * Suchkriterium des QR-Abhol-Scans (REQ-SC-030) — NICHT die Auftrags-UUID.
   */
  receiptNumber?:   string;
  /** Tischangabe. */
  restaurantTable?: PayfixRestaurantTable;
  /**
   * Tischnummer, wie die Kasse sie zusaetzlich liefert — als Zahl (`12`) oder
   * als Ziffernfolge (`"12"`). An den am 2026-10-08 gemessenen Kitchen-TV-
   * Auftraegen der Produktion steht die Tischnummer ALLEIN hier,
   * `restaurantTable` ist dort `{}` (REQ-SC-066 Praez. 1, Nachfuehrung 1 (g)).
   * Typergaenzung, kein Datenmodell (Nachf. 1 (f')): gelesen wird das Feld in
   * `dashboard/src` allein von `tischNummer` (`utils/kitchenView.ts`), als
   * Ersatz fuer `restaurantTable.number`; serverseitig liest es
   * `readWorkReceiptTableNumber` (`firebase/functions/src/tableRanges.ts`,
   * REQ-LC-048) nach derselben Wertregel. Der Typ ist bewusst weit: was
   * keine ganze Zahl ist, gilt als fehlend.
   */
  tableName?:       number | string | null;
  /** Druckermodell aus Sicht der Kasse (z. B. "TM_P20", "TM_U220", "SUNMI"). */
  printer_model?:   string;
  /** Zieladresse aus Sicht der Kasse (IP). Printerque routet ueber `printerId`. */
  printer?:         string;
  /** Auftragspositionen des Arbeitsbons. */
  items?:           PayfixItem[];
  /** 1 = Preise auf dem Arbeitsbon drucken (Bestands-Schalter, Wert 1 = ein). */
  price_in_workreceipts?: number;
  /** Sunmi-Puffermodus (Bestands-Schalter, Wert 1 = ein). */
  sunmi?:           number;
  /** Takeaway-Beleg. */
  is_takeaway?:     boolean;
  /**
   * Bestell-Kennung der Kasse; Inhalt des Bon-QR-Drucks (AUSGABE, REQ-SC-030).
   * NICHT die Firestore-Dokumentkennung des Auftrags.
   */
  id?:              number;

  // ── Kassenbon (Bestands-Beleg) ─────────────────────────────────────────────
  /**
   * Befehlsliste eines Kassenbons (REQ-PR-002). Ihr Vorhandensein unterscheidet
   * Kassenbon von Arbeitsbon (`isKassenbon`, REQ-LC-006/REQ-LC-007) und schliesst
   * Bildschirm-Kanaele als Ziel aus (REQ-SC-002). Der Inhalt ist bewusst
   * untypisiert: Printerque wertet ihn nicht aus, sondern reicht ihn durch.
   */
  epson_objects?:   unknown[];

  // ── Idempotenz und Herkunft (Bestands-Beleg) ───────────────────────────────
  /** Auftrags-UUID der Kasse (REQ-LC-003, REQ-RS-011); Alt-Software-Feldname. */
  uuid?:            string;
  /** Zweiter Alt-Software-Feldname derselben Auftrags-UUID (REQ-PR-035). */
  job_uuid?:        string;
  /** OrderMood-Herkunft (REQ-DM-033, REQ-SC-040); nur `true` zaehlt. */
  isFromOrderMood?:      boolean;
  /** OrderMood-Tischbestellung (REQ-DM-033); nur `true` zaehlt. */
  isFromOrderMoodTable?: boolean;

  /** Jedes weitere Feld der Kasse — unveraendert durchgereicht (REQ-LC-007). */
  [key: string]:  unknown;
}

// ── Festgehaltener Entfall am Beleg (REQ-PR-037, REQ-DM-030 Ergaenzung) ───────
// Ein Befehl, den der Renderer benennen, am Zielmodell aber nicht ausfuehren
// konnte, wird am Auftrag VERMERKT statt still verschluckt. Erzeuger ist
// ausschliesslich der Druckserver (print-server/src/render/** -> `report`,
// print-server/src/printer.js `toOmittedCommands`); die Werte bleiben
// technisch-englisch, uebersetzt wird nur die Oberflaeche (REQ-OV-002).

/**
 * Abschliessender Wertevorrat der Entfall-Gruende, deckungsgleich mit
 * `ZULAESSIGE_REASONS` in print-server/src/render/index.js (dort abschliessend
 * gesetzt und beim Rendern erzwungen).
 *
 * `unknownOpcode` IST seit dem 2026-08-28 ein Grund (REQ-PR-037 Praezisierung 3,
 * REQ-DM-030 Ergaenzung 2026-08-28, Entscheid des Auftraggebers zu OP-179): der
 * ausfuehrende Renderer kennt diesen `write`-Wert nicht, das Objekt ist
 * uebersprungen worden, der Beleg ist gedruckt und der Auftrag `completed`.
 *
 * HISTORISCH (Fassung bis zum 2026-08-28, berichtigt statt geloescht,
 * REQ-OPS-036 Praez. 6 — sie beschreibt NICHT die heutige Lage):
 * „`unknownOpcode` ist bewusst KEIN Grund, sondern ein Wurf — ein unbekannter
 * Befehl fuehrt zu `failed`, nicht zu einem Vermerk."
 *
 * `logoUnreadable` IST seit dem 2026-10-15 der SECHSTE Grund (REQ-PR-037
 * Praezisierung 4, REQ-DM-030 Ergaenzung 2026-10-15, Grundlage REQ-PR-039 (g)/
 * (h)): das Logo des Mandanten ist gesetzt, aber nicht lesbar; der Beleg ist
 * ohne Logo gedruckt. Er ist der erste Grund, der KEINEN Befehl der Liste
 * betrifft — der Eintrag lautet `{ index: null, opcode: null, reason:
 * 'logoUnreadable' }`, und die Auftragsliste zeigt ihn ohne Position
 * (`entry.index !== null` in `views/JobsView.vue`). Die Liste bewegt sich in
 * EINEM Commit mit `ZULAESSIGE_REASONS` im Druckserver (Praez. 4 (4)).
 */
export const OMITTED_COMMAND_REASONS = [
  'unsupportedByModel',
  'emptyPayload',
  'invalidParameter',
  'unflushedBuffer',
  'unknownOpcode',
  'logoUnreadable',
] as const;
export type OmittedCommandReason = (typeof OMITTED_COMMAND_REASONS)[number];

/**
 * Abschliessende Naeherbestimmung eines Grundes (REQ-PR-037 Praezisierung 2 (d)).
 * `detail` traegt KEINE eigene Semantik: es qualifiziert einen bestehenden Grund
 * (der verschluckte Vollschnitt, der nicht ausgegebene Zeilenpuffer) und steht
 * nie allein.
 */
export const OMITTED_COMMAND_DETAILS = ['cut', 'textline'] as const;
export type OmittedCommandDetail = (typeof OMITTED_COMMAND_DETAILS)[number];

// ── Woran der Erfolg gemessen wurde (REQ-DM-030 Ergaenzung 2026-09-11) ────────
/**
 * Abschliessender Wertevorrat des Feldes `deliveryConfirmation`
 * (REQ-DM-030 Ergaenzung 2026-09-11, REQ-LC-040 Praez. 1).
 *
 *   • `printerResponse` — der Drucker hat auf einen in der Reihenfolge des
 *     Empfangspuffers verarbeiteten Antwortbefehl geantwortet, und die Antwort
 *     war diesem Auftrag zuzuordnen.
 *   • `transportOnly`   — das Modell hat KEINE am Geraet gemessene
 *     Antwortfaehigkeit (REQ-RP-047); der Auftrag gilt mit dem Hinausschreiben
 *     der Bytes als zugestellt.
 *
 * Technisch-englisch wie jeder Wert in der Datenbank (REQ-OV-002); uebersetzt
 * wird ausschliesslich in der Oberflaeche.
 */
export const DELIVERY_CONFIRMATIONS = ['printerResponse', 'transportOnly'] as const;
export type DeliveryConfirmation = (typeof DELIVERY_CONFIRMATIONS)[number];

/** Ein festgehaltener Entfall — ein Eintrag je nicht ausgefuehrtem Befehl. */
export interface OmittedCommand {
  /**
   * Position des Befehls in der Befehlsliste des Belegs (0-basiert) — oder
   * `null` an genau einem Grund: `logoUnreadable` betrifft keinen Befehl der
   * Liste (REQ-DM-030 Ergaenzung 2026-10-15, REQ-PR-037 Praez. 4 (2)).
   */
  index:   number | null;
  /**
   * Opcode des Befehls, oder `null`, wenn kein einzelner Befehl zuzuordnen ist
   * (der Druckserver normalisiert `undefined` auf `null`, damit ein Vermerk den
   * Abschluss-Schreibvorgang nie umwirft).
   */
  opcode:  number | null;
  reason:  OmittedCommandReason;
  detail?: OmittedCommandDetail;
}

// ── Kuechensicht (REQ-SC-062) ─────────────────────────────────────────────────
// Die Angaben, die das KUECHENPERSONAL an einem Auftrag erzeugt — streng
// getrennt von den Kassennutzdaten.

/**
 * Eine angenommene nachtraegliche Aenderung einer Bestellung (REQ-SC-072).
 *
 * `count` ist die angenommene ANZAHL (nicht die verbleibende Menge); die
 * angezeigte Menge ergibt sich daraus in der Sicht. Gefuehrt wird je
 * Positionsschluessel nach REQ-SC-064.
 */
export interface KitchenAcceptedChange {
  /** Positionsschluessel nach REQ-SC-064 (`<articleId>_<gangId>[_[Signatur]]`). */
  itemKey: string;
  /**
   * Art der Aenderung: Nachbestellung oder Stornierung.
   *
   * ⚠ `added` WIRD VON DER SICHT NICHT ERZEUGT (REQ-SC-072 Praezisierung 1
   * (c), 2026-08-11): die Art hat im Alt-Bestand KEINE Entstehungsstelle —
   * `type: 'added'` kommt dort nur im Typ und im Anzeigezweig vor. Der Wert
   * bleibt im Feldbestand und in der Darstellung erhalten; erzeugt wird er
   * nicht, und `kitchenaction` weist ihn ab, solange kein Entscheid einen Fall
   * dafuer benennt.
   */
  kind:    'added' | 'cancelled';
  /** Angenommene Anzahl (immer positiv; die Richtung steckt in `kind`). */
  count:   number;
  /**
   * DIE KENNUNG DES STORNO-AUFTRAGS — des Auftrags, der die negative Menge
   * fuehrt (REQ-SC-072 Praezisierung 1 (d), NEU am 2026-08-11).
   *
   * ⚠ SIE IST DIE VIERTE ANGABE, OHNE DIE DIE ANNAHME NICHT IDEMPOTENT IST:
   * zwei getrennte Stornierungen von je 1 am selben Positionsschluessel waeren
   * ueber Schluessel, Art und Anzahl von EINER zweimal angenommenen nicht zu
   * unterscheiden — die zweite Stornierung verschwaende ungesehen. Der Vermerk
   * liegt am ZIELAUFTRAG (dem mit der Ursprungsposition), nicht am
   * Storno-Auftrag; bei einer negativen Ausgangsmenge OHNE hinterlegte
   * Aenderung sind beide derselbe Auftrag.
   */
  sourceJobId: string;
  /**
   * Zeitpunkt der Annahme.
   *
   * ⚠ EIN FERTIGER `Timestamp`, NIE `serverTimestamp()` (REQ-SC-062
   * Praezisierung 1): Firestore laesst den Platzhalter INNERHALB EINES
   * LISTENELEMENTS nicht zu, und der Schreibvorgang scheitert zur Laufzeit —
   * kein reiner Modultest zeigt es. Gebildet wird er EINMAL im Rahmen von
   * `kitchenaction` und der Aktion uebergeben. `kitchen.updatedAt` und das
   * Wurzelfeld `updatedAt` bleiben unveraendert `serverTimestamp()`.
   */
  at:      Timestamp;
}

/**
 * DIE KUECHENSICHT NEBEN DEN NUTZDATEN (REQ-SC-062, REQ-SC-061).
 *
 * FELDVERTRAG (verbindlich):
 *   • Geschrieben wird `kitchen` AUSSCHLIESSLICH von der Cloud Function
 *     `kitchenaction` ueber das Admin SDK (REQ-SC-063). Die Firestore-Rules
 *     bleiben unveraendert und erlauben einem Anzeige-Token weiterhin nur
 *     `workStatus`/`updatedAt`/`readyAt` — `kitchen` ist ueber das Web-SDK
 *     NICHT schreibbar, und das ist Absicht, kein Versehen.
 *   • FEHLT das Feld, ist der Auftrag von der Kuechensicht unberuehrt; ein
 *     fehlendes Unterfeld ist die LEERE LISTE. Die Sicht darf also weder ein
 *     Feld noch eine leere Liste erwarten.
 *   • `payload` bleibt dabei in jedem Fall unveraendert, und kein
 *     `printJobs`-Dokument wird geloescht (REQ-SC-061). Das ist die Bedingung,
 *     unter der die Struktureingriffe der Alt-Loesung (Gang-Aufteilung,
 *     Tisch-Zusammenfuehrung, Aenderungsannahme) mit REQ-LC-007 und REQ-RS-001
 *     vereinbar sind: die fachliche Wirkung bleibt 1:1, nur ihr Traeger
 *     wechselt vom Dokument auf die Sicht.
 */
export interface KitchenView {
  /**
   * Positionsschluessel (REQ-SC-064) der Positionen, die als fertig gemeldet
   * sind (REQ-SC-070) bzw. im Fastline-Scan-Dialog abgehakt wurden
   * (REQ-SC-086 — dieselbe Bauform, kein zweiter Zaehlweg).
   */
  readyItems?:      string[];
  /** Angenommene nachtraegliche Aenderungen (REQ-SC-072). */
  acceptedChanges?: KitchenAcceptedChange[];
  /**
   * Gang-Kennungen, die einzeln in Bearbeitung genommen wurden (REQ-SC-068).
   *
   * ⚠ ZEICHENKETTEN, NICHT ZAHLEN — berichtigt am 2026-08-11 (T-M12.8). Hier
   * stand `number[]`; das war der Stand vor REQ-SC-064 Praezisierung 2
   * (2026-08-10, gebaut mit T-M12.41). Die Kennung ist seither die kanonische
   * ZEICHENKETTE aus `gangSchluessel` — `'0'`, `'2'`, `'2.5'`, `'Ürli'` —,
   * dieselbe, die im Positionsschluessel steht (REQ-SC-064). Eine `number`
   * daneben waere die zweite Lesart desselben Feldes und damit genau der
   * Fehler, den Praezisierung 2 abgestellt hat: ein unter `2` abgelegter Gang
   * waere nach dem Neuladen nicht wiederzufinden.
   */
  startedGangs?:    string[];
  /**
   * Auftrags-Kennungen, mit denen dieser Auftrag als EIN Ticket gefuehrt wird
   * (REQ-SC-069). Gefuehrt an BEIDEN beteiligten Auftraegen; beide Dokumente
   * bleiben bestehen.
   */
  mergedWith?:      string[];
  /** Zeitpunkt der letzten Kuechensicht-Aenderung (Server-Zeitstempel). */
  updatedAt?:       Timestamp;
}

// ── PrintJob ──────────────────────────────────────────────────────────────────
// Schluesselfelder gemaess REQ-DM-030.

export interface PrintJob {
  id:            string;
  /** Zielkanal (Pflicht, REQ-DM-030). */
  printerId:     string;
  /** Denormalisiert; nur bei Ausgabeart `print` gesetzt (REQ-DM-030). */
  printserverId?: string;
  /** Herkunftskasse. */
  registerId:    string;
  companyId:     string;
  /** Standort des Auftrags. */
  locationId?:   string;
  /** Zustellstatus (REQ-DM-040). */
  status:        PrintJobStatus;
  /**
   * Bearbeitungsstatus (REQ-DM-041); nur Bestellungen/Arbeitsbons.
   * Kassenbons tragen KEINEN workStatus (REQ-DM-042).
   */
  workStatus?:   WorkStatus;
  /**
   * Zeitpunkt des Uebergangs auf `workStatus: 'ready'` (REQ-SC-013, REQ-DM-060).
   *
   * FELDVERTRAG (verbindlich): Wer eine Bestellung auf `ready` setzt (Kitchen-TV-
   * Klick durch das Kuechenpersonal, Abholscreen, oder eine Payfix-eigene
   * Oberflaeche), MUSS in derselben Aktualisierung `readyAt` auf einen
   * Server-Zeitstempel setzen. Die serverseitige Funktion `completereadyorders`
   * misst gegen diesen Zeitpunkt die konfigurierbare Anzeigedauer
   * (`printers.abholDisplaySec`, Standard 60 s) und setzt die Bestellung danach
   * automatisch auf `done` – auch bei ausgeschaltetem Bildschirm.
   *
   * Bewusste Entscheidung (T-M5.2): Die `readyAt`-Setzung erfolgt CLIENTSEITIG
   * beim Uebergang (nicht per Firestore-Trigger), um die hohe Schreiblast auf
   * `printJobs` nicht mit einem trigger-je-Schreibvorgang zu belasten. Fehlt
   * `readyAt` (Vertragsverletzung/Altbestand), faellt `completereadyorders`
   * ersatzweise auf `updatedAt` zurueck, damit keine `ready`-Bestellung ewig
   * haengen bleibt (Robustheit statt Praezision).
   */
  readyAt?:      Timestamp | null;
  /**
   * Zeitpunkt des Uebergangs auf `workStatus: 'done'` (REQ-DM-067, T-M22.3,
   * seit dem 2026-08-18) — „wann wurde diese Bestellung abgeschlossen?".
   *
   * Er verhaelt sich zu `done` wie `readyAt` zu `ready` und `printedAt` zu
   * `completed`: der Zeitpunkt eines UEBERGANGS, kein Statuswert und keine
   * dritte Dimension.
   *
   * FELDVERTRAG, sechs bindende Festlegungen (REQ-DM-067 (1)–(6)):
   *   1. Es entsteht bei JEDEM Uebergang auf `done`, im selben Schreibvorgang;
   *      die Menge der Schreibstellen ist abschliessend SECHS
   *      (`completereadyorders`, `completestaleorders`, der Zweig
   *      `ready → done` in `screenScan`, der Auto-Abschluss nach dem Abraeumen
   *      aller Positionen, `completeHandover`, der Sammelabschluss). Eine
   *      siebte ist ein Entscheid und kein Nebeneffekt.
   *   2. EIN SERVERZEITSTEMPEL, geschrieben AUSSCHLIESSLICH serverseitig. ⚠ Das
   *      Dashboard schreibt es NICHT — anders als `readyAt`, das clientseitig
   *      beim Uebergang gesetzt wird (siehe oben). Der Weg der Bildschirm-
   *      Ansichten laeuft ueber `kitchenaction` und `screenScan`.
   *   3. KEINE Rules-Aenderung: ein Anzeige-Token darf ueber das Web-SDK
   *      unveraendert nur `workStatus`, `updatedAt` und `readyAt` schreiben
   *      (REQ-SC-063). Die Leitplanke der FUNKTION waechst dagegen um dieses
   *      Feld (siebtes Wurzelfeld, REQ-SC-061).
   *   4. KEIN Index — das Feld wird nie abgefragt, nur gelesen; es steht in
   *      keiner `where`- und keiner `orderBy`-Bedingung (REQ-OPS-039).
   *   5. DER BESTAND WIRD NICHT NACHGEZOGEN: Bestellungen, die vor der
   *      Auslieferung auf `done` gegangen sind, tragen das Feld nicht. Deshalb
   *      — und nur deshalb — faellt die Anzeige auf `scannedAt` zurueck
   *      (REQ-SC-082 Praez. 4 (c), `utils/screenScanResult.ts`).
   *   6. ES STEUERT NICHTS. Seine Leser sind die Antwort von `screenScan` und
   *      die Anzeige. Ein fehlendes Feld und jeder Fremdtyp ergeben `null` —
   *      kein Ersatzwert, insbesondere NICHT `updatedAt`.
   *
   * ABGRENZUNG: `doneAt` gehoert zur BEARBEITUNGS-Dimension (`workStatus`),
   * `completedAt`/`printedAt` zur ZUSTELL-Dimension (`status`). Ob jeder
   * `completed`-Auftrag kuenftig eine einheitliche Abschlusszeit tragen soll,
   * ist als OP-137 gestellt und von diesem Feld NICHT beantwortet.
   */
  doneAt?:       Timestamp | null;
  /**
   * Die Kuechensicht NEBEN den Nutzdaten (REQ-SC-062, REQ-DM-030 Ergaenzung).
   *
   * Optional: fehlt das Feld, ist der Auftrag von der Kuechensicht unberuehrt.
   * Geschrieben ausschliesslich von der Cloud Function `kitchenaction`
   * (REQ-SC-063) — siehe den Feldvertrag an `KitchenView`.
   */
  kitchen?:      KitchenView;
  /** Belegnummer. */
  receiptNumber?: string;
  /**
   * DIE KENNUNG EINER ORDERMOOD-BESTELLUNG (REQ-DM-066, T-M20.14).
   *
   * Eine Zeichenkette, getrimmt, geschrieben AUSSCHLIESSLICH vom Auftragseingang
   * bei der Anlage des Dokuments — Zeichen fuer Zeichen dieselbe Bauform wie
   * `receiptNumber` und aus demselben Grund: eine Abfrage kann nur finden, was am
   * Dokument steht, nicht was in den Nutzdaten liegt.
   *
   * FELDVERTRAG, fuenf bindende Festlegungen (REQ-DM-066 (1)–(5)):
   *   1. Uebernommen wird `payload.invoiceNumber`, getrimmt, und NUR wenn er
   *      vorhanden und nicht leer ist — nie als leere Zeichenkette, nie als
   *      `null`. Ein Auftrag ohne diesen Wert traegt das Feld nicht; das IST die
   *      Aussage „diese Bestellung fuehrt keine OrderMood-Kennung".
   *   2. Der Wert wird NICHT ausgelegt und NICHT umgeformt (Beispiel von Payfix:
   *      `"vxylk0z"` — alphanumerisch, ohne erkennbare Struktur): nichts wird in
   *      Grossbuchstaben gewandelt, nichts aufgefuellt, nichts in eine Zahl
   *      gewandelt.
   *   3. Es tritt NEBEN `receiptNumber` und ersetzt es nicht; beide koennen am
   *      selben Dokument stehen. Die Ausschliesslichkeit wird ausdruecklich NICHT
   *      erzwungen.
   *   4. Der Suchweg braucht den zusammengesetzten Index `companyId + printerId +
   *      invoiceNumber + status + createdAt DESC` (die Spiegelung des
   *      bestehenden mit `receiptNumber`).
   *   5. Das Feld ist BESCHREIBEND und steuert nichts: sein einziger Leser ist
   *      die Suche von `screenScan` an einem Kanal im Fastline-Modus
   *      (REQ-SC-094 (3)). Es ist KEIN Ersatz fuer `jobUuid` (REQ-DM-032) und
   *      kein Bestandteil des Fingerabdrucks.
   *
   * BENANNTE GRENZE: Bestands-OrderMood-Bestellungen, die vor der Auslieferung
   * angelegt wurden, tragen das Feld nicht und sind ueber den Scan nicht zu
   * finden. Eine Datenwanderung ist ausdruecklich nicht Teil der Anforderung.
   */
  invoiceNumber?: string;
  /**
   * Idempotenz-/Bestell-Kennung (Auftrags-UUID der Kasse), indexiert (REQ-DM-032).
   * Kanonischer Feldname; dient der Deduplizierung im Webhook.
   *
   * Abgrenzung zu den zwei QR-Konzepten (REQ-SC-030, strikt getrennt): Dies ist
   * NICHT das Suchkriterium des QR-Abhol-Scans – der QR-Abhol-Scan (EINGABE) sucht
   * ueber die Belegnummer (`receiptNumber`), nicht ueber diese UUID. Der Bon-QR-
   * Druck (AUSGABE, Feld `qrCode` am printers-Dokument) ist ein davon unabhaengiges
   * Ausgabe-Anliegen.
   */
  jobUuid?:      string;
  /**
   * QR-Abhol-Scan (EINGABE, REQ-SC-030/031/041): Zeitpunkt, zu dem eine Bestellung
   * am Bildschirm (Kitchen TV / Abholscreen) ueber ihre Belegnummer eingelesen
   * wurde. Der eingelesene Code stammt IMMER von ausserhalb (Fastline-Kiosk-Zettel,
   * gedruckte Abholnummer), NIE vom eigenen Bon-QR-Druck (REQ-SC-031). Strikt
   * getrennt vom Bon-QR-Druck (AUSGABE, Feld `qrCode` am printers-Dokument).
   */
  scannedAt?:    Timestamp | null;
  /** The raw Payfix receipt payload (Beleg-Nutzdaten). */
  payload:       PayfixPayload;
  /** Populated by the print server on failure. */
  errorMessage?: string;
  /**
   * Festgehaltener Entfall (REQ-DM-030 Ergaenzung, REQ-PR-037): Befehle, die der
   * Beleg fuehrte, das Zielmodell aber nicht ausfuehren konnte.
   *
   * FELDVERTRAG (verbindlich): geschrieben NUR vom Druckserver, NUR auf dem
   * `completed`-Pfad und NIE leer. Ein FEHLENDES Feld heisst „der Beleg war
   * vollstaendig" — eine leere Liste wird nicht geschrieben, die Oberflaeche darf
   * sie also auch nicht erwarten. Der Zustellstatus bleibt `completed`: ein
   * unvollstaendiger Beleg ist kein Fehlschlag, aber er darf nicht als
   * vollstaendig durchgehen (REQ-PS-021).
   */
  omittedCommands?: OmittedCommand[];
  /** Herkunftskennzeichen (z. B. OrderMood). */
  source:        PrintJobSource;
  /**
   * OrderMood-Herkunfts-Flags (REQ-DM-033, REQ-SC-040). Der `payfixwebhook`
   * persistiert sie 1:1 am Auftrag, damit der Payfix-Abholscreen Fastline von
   * Tisch-OrderMood unterscheiden kann. Nur bei OrderMood-Eingaengen gesetzt.
   */
  isFromOrderMood?:      boolean;
  isFromOrderMoodTable?: boolean;
  /**
   * DAS FAST-LANE-KENNZEICHEN (REQ-DM-065, T-M20.3).
   *
   * FELDVERTRAG (verbindlich, wortgleich mit der Spec — eine Quelle, kein
   * zweiter Vertrag):
   *   1. NUR `true` WIRD ABGELEGT. Ein Auftrag ohne Fast-Lane-Herkunft traegt
   *      das Feld NICHT — nie hart `false`. Ein fehlendes Feld IST die Aussage
   *      „nicht Fast Lane"; eine Abfrage auf `fastLane == true` findet genau
   *      die Menge (dieselbe Bauform wie `isFromOrderMood`).
   *   2. GESCHRIEBEN AUSSCHLIESSLICH VOM AUFTRAGSEINGANG (`payfixwebhook`,
   *      `payfixlegacywebhook`) BEI DER ANLAGE, und `true` genau dann, wenn
   *      BEIDES zutrifft: der Zielkanal laeuft im Fastline-Modus (REQ-SC-089)
   *      UND der Auftrag traegt OrderMood-Herkunft (`isFromOrderMood ||
   *      isFromOrderMoodTable`, REQ-DM-033).
   *   3. ES IST EINE MOMENTAUFNAHME, keine laufende Ableitung: wird der
   *      Fastline-Modus des Kanals spaeter umgeschaltet, aendern BESTEHENDE
   *      Auftraege ihr Kennzeichen nicht. Eine Ansicht, die `fastLane` zur
   *      Laufzeit aus Kanal und Herkunft neu bildet, ist ein zweiter
   *      Feldvertrag ueber dieselbe Tatsache und verboten.
   *   4. ES STEUERT NICHTS. Ob der Scan abschliesst, ob die Sicherheits-
   *      Rueckfrage kommt und ob der Dialog eine Abschluss-Schaltflaeche
   *      traegt, entscheidet ausschliesslich der KANAL (REQ-SC-089 bis
   *      REQ-SC-091); die Unterscheidung nach Herkunft traegt `source`
   *      beziehungsweise die zwei Flags (REQ-SC-090 (3), REQ-SC-095).
   *      `fastLane` ist Herkunfts- und AUSWERTUNGSmerkmal (Filter, Bericht,
   *      Nachvollzug) und wird nicht zur zweiten Steuerung ausgebaut.
   *   5. Ordermood sendet KEIN eigenes Fast-Lane-Signal (Payfix-Antwort vom
   *      2026-08-18 zu OP-164) — Punkt 2 ist und bleibt die Quelle.
   */
  fastLane?:     boolean;
  createdAt:     Timestamp;
  updatedAt:     Timestamp;
  // ── Uebernahme-Felder (Claiming) ────────────────────────────────────────────
  /** Wer den Auftrag uebernommen hat (Druckserver-Kennung). */
  claimedBy?:    string;
  /** Zeitpunkt der Uebernahme (pending → printing). */
  claimedAt?:    Timestamp | null;
  /** Zeitpunkt der bestaetigten Zustellung (printing → completed). */
  printedAt?:    Timestamp | null;
  // ── Halte-Kennzeichen und Freigabe (REQ-DM-030 Ergaenzung 2026-08-07) ───────
  /**
   * Das HALTE-KENNZEICHEN (REQ-LC-009): der Auftrag wird von der Zustellung
   * zurueckgehalten, bis die Kueche ihn freigibt (Filter REQ-LC-013, Freigabe
   * REQ-LC-014).
   *
   * FELDVERTRAG (verbindlich): es steht an JEDEM `printJobs`-Dokument, auch
   * dort, wo es immer `false` ist — Firestore kann nicht auf „Feld fehlt"
   * filtern, und ein Dokument ohne dieses Feld ist fuer den Zustell-Listener
   * UNSICHTBAR (es wuerde nie gedruckt und nach vier Stunden verfallen).
   * Geschrieben wird es an ALLEN DREI Anlagestellen (REQ-LC-009 Praez. 1):
   * dem Auftragseingang `ingestPrintJob` (nach den vier Bedingungen), dem
   * Nachdruck `buildResendJobData` und der Anlage aus der Verwaltung
   * `createPrintJob` — die beiden letzten immer `false`, ohne Bedingung.
   *
   * OPTIONAL, und zwar genau aus EINEM Grund: ein fehlender Wert kommt
   * ausschliesslich an Dokumenten aus der Zeit VOR dem Deploy dieses Feldes
   * vor (die einmalige Nachziehung des Bestands ist T-M15.9). Ein fehlender
   * Wert ist wie jeder Fremdtyp als „nicht gesetzt" zu lesen (REQ-SC-016
   * Praez. 1c) — er ist KEINE Erlaubnis, das Feld bei einer Neuanlage
   * wegzulassen.
   */
  holdUntilReady?: boolean;
  /**
   * Zeitpunkt der FREIGABE eines zuvor gehaltenen Auftrags (REQ-DM-030
   * Ergaenzung, REQ-LC-014). Er ist die Bezugszeit des Verfalls fuer einen
   * gehaltenen Auftrag (REQ-RS-053) und wird genau einmal gesetzt (idempotent).
   *
   * Das Feld entsteht ERST mit der Freigabe (T-M15.4) und wird bei der Anlage
   * nie geschrieben — an keiner der drei Anlagestellen. Fehlt es, ist der
   * Auftrag nie gehalten worden ODER nie freigegeben; die beiden Faelle sind an
   * `holdUntilReady` unterscheidbar.
   */
  releasedAt?:   Timestamp | null;
  // ── Zustellung OHNE Druck (REQ-LC-043, REQ-DM-030 Ergaenzung 2026-08-07) ────
  /**
   * „OHNE DRUCK ZUGESTELLT" (REQ-LC-043): der Auftrag traegt `completed`, aber
   * KEIN `printedAt` — es war nie ein Geraet beteiligt. Der Fall entsteht an
   * einem Kanal der Ausgabeart `print` mit zugeschalteter Kitchen-TV-Ansicht,
   * der den Druck abbestellt hat (`kitchenTvLight` oder
   * `disablePrintingWhenKitchenTv`).
   *
   * ERZEUGER IST AUSSCHLIESSLICH `ingestPrintJob` (beide Eingaenge, REQ-LC-008).
   * An einem Kanal der Ausgabeart `kitchenTv` entsteht das Feld NICHT
   * (REQ-SC-020: dort ist die Speicherung die Zustellung, `completed` ist die
   * vollstaendige Aussage). Der Nachdruck traegt es nicht weiter —
   * `buildResendJobData` ist eine Positivliste.
   *
   * ZUSICHERUNG, die es traegt (REQ-LC-044): ein `print`-Auftrag mit
   * `completed` fuehrt ENTWEDER `printedAt` ODER dieses Feld; keines von beiden
   * ist ein Defekt. Ein fehlender Wert und jeder Fremdtyp heissen „nicht
   * gesetzt" (REQ-SC-016 Praez. 1c).
   */
  deliveredWithoutPrint?: boolean;
  /**
   * Zeitpunkt der ZUSTELLUNG OHNE DRUCK (REQ-DM-030, Nachfuehrung 2026-08-12).
   *
   * Er tritt an die Stelle, die sonst `printedAt` einnaehme, und ersetzt es
   * NICHT in seiner Bedeutung: `printedAt` sagt „ein Geraet hat gedruckt und es
   * bestaetigt" (REQ-LC-040), `completedAt` sagt „der Auftrag ist zugestellt
   * worden, ohne dass ein Geraet beteiligt war".
   *
   * ⚠ KEINE ALLGEMEINE PFLICHT: das Feld steht NICHT an jedem `completed`-
   * Auftrag. Es hat genau EINE Schreibstelle — den Zweig aus REQ-LC-043 (a) im
   * Auftragseingang. Weder der Druckserver noch der Bildschirm-Zweig sind um es
   * zu erweitern. Ob jeder `completed`-Auftrag kuenftig eine einheitliche
   * Abschlusszeit tragen soll, ist als OP-137 gestellt und nicht beantwortet;
   * bis dahin liest jede Anzeige einer Abschlusszeit BEIDE Felder.
   */
  completedAt?:  Timestamp | null;
  // ── Woran der Erfolg gemessen wurde (REQ-DM-030 Ergaenzung 2026-09-11) ─────
  /**
   * DIE ZUSTELLBESTAETIGUNG (REQ-DM-030 Ergaenzung 2026-09-11, REQ-LC-040
   * Praez. 1): sie sagt, WORAN der Erfolg gemessen worden ist — nicht, OB er
   * eingetreten ist. Das sagt unveraendert `status` (REQ-LC-040, REQ-LC-044).
   *
   * FELDVERTRAG (verbindlich, wortgleich mit der Spec):
   *   1. GESCHRIEBEN ausschliesslich vom DRUCKSERVER, im selben Schreibvorgang
   *      wie `completed`/`printedAt`, und ausschliesslich an Auftraegen der
   *      Ausgabeart `print`, an denen wirklich gedruckt wurde.
   *   2. IMMER — auch im Normalfall `printerResponse`. Sonst waere ein
   *      fehlendes Feld nicht von einem noch nicht ausgerollten Geraetestand zu
   *      unterscheiden.
   *   3. ES ENTSTEHT NICHT am Bildschirm-Kanal (`kitchenTv`, REQ-SC-020), nicht
   *      am Auftrag mit `deliveredWithoutPrint` (REQ-LC-043) und nicht auf dem
   *      `failed`-Pfad.
   *   4. EIN FEHLENDES FELD HEISST GENAU EINES: „das Dokument stammt aus der
   *      Zeit vor dieser Ergaenzung" — es heisst NICHT „bestaetigt" und NICHT
   *      „unbestaetigt".
   *
   * Das Feld erzeugt KEINE Fehler-Semantik; seine Anzeige regelt REQ-PS-021
   * Praez. 2 (neben dem Zustellstatus, neutraler Ton).
   */
  deliveryConfirmation?: DeliveryConfirmation;
}

// ── User ─────────────────────────────────────────────────────────────────
// Rollen gemaess REQ-DM-070: admin / user. Was jede Rolle DARF, steht bei
// REQ-SEC-027; der Anspruch, aus dem die Oberflaeche sie liest, bei
// REQ-SEC-028 (`utils/anspruch.ts`).
//
// ⚠ BERICHTIGT AM 2026-09-01 (T-M30.7, REQ-DM-016 Praez. 1, REQ-OPS-048):
// bis dahin stand hier „Rollen gemaess REQ-DM-060: admin / operator / user."
// **REQ-DM-060 ist die Kopplungsregel der beiden Printer-Typen und hat mit
// Rollen nichts zu tun**; die richtige Zitierstelle ist REQ-DM-070. Ein
// Kommentar, der eine falsche ID zitiert, ist ein Textbefund und wird beim Bau
// richtiggestellt.

/**
 * ZWEI Rollenwerte, technisch-englisch (REQ-DM-070 (a), REQ-OV-002).
 *
 * ⚠ **`operator` IST ABGERAEUMT** (REQ-DM-070 (b), 2026-09-01): sie war
 * nirgends verhaltenswirksam ausser als Vorgabewert in `UserDetailView.vue`,
 * und der ist seither `user`. **Ein fehlender oder unbekannter Wert bedeutet
 * `user`** — die engere der beiden Rollen.
 */
export type UserRole = 'admin' | 'user';

// ⚠ ENTFALLEN AM 2026-09-01 (T-M30.7, REQ-DM-070 (d)): `CompanyPermission`
// (`edit` | `read`). Das Feld war ein TOTES Datenfeld — geschrieben und als
// Spalte „Berechtigung" gezeigt, aber an keiner Stelle ausgewertet: weder in
// den Rules, noch in den Cloud Functions, noch im Dashboard. *Berichtigt statt
// geloescht (REQ-OPS-036 Praez. 6).*

export interface AppUser {
  uid:          string;
  email:        string;
  displayName?: string;
  role:         UserRole;
  // ⚠ ENTFALLEN AM 2026-09-01 (T-M30.7, REQ-DM-070 (e)): `companyId: string`.
  // Es trug den „Mandanten-Kontext" eines Benutzers, wurde beim Erstlogin als
  // LEERE Zeichenkette angelegt und von nichts Wesentlichem ausgewertet; **es
  // kann die 1-n-Beziehung strukturell nicht abbilden** und waere neben
  // `companies/{id}/members` eine zweite Fuehrung derselben Tatsache. Die
  // Mandanten stehen im Anspruch (`companyIds`, REQ-SEC-028), der im Dashboard
  // gewaehlte Mandant bleibt eine Sitzungsangabe (`pf_company_id`).
  notiz?:       string;    // Internal notes about this user
  createdAt:    Timestamp;
  updatedAt:    Timestamp;
}

/**
 * Per-company membership – stored in companies/{companyId}/members/{userId}.
 *
 * **Der Feldvertrag ist ABSCHLIESSEND** (REQ-DM-070 (c)): `userId`,
 * `displayName`, `email`, `addedAt`. Die Dokument-Kennung ist die
 * Benutzer-Kennung. *`permission` ist am 2026-09-01 entfallen — siehe oben.*
 */
export interface CompanyMember {
  userId:      string;
  displayName: string;
  email:       string;
  addedAt?:    Timestamp;
}

// ── ApiToken ────────────────────────────────────────────────────────────────
// Zugriffstoken fuer die oeffentliche REST-API (REQ-DM-017, REQ-SEC-002).
// Es wird NIEMALS der Klartext gespeichert, sondern nur der SHA-256-Hash
// (`tokenHash`) plus ein nicht-geheimes Praefix (`tokenPrefix`) zur Anzeige/
// Identifikation. Der Klartext (`token`) wird ausschliesslich einmalig in der
// Antwort von `createapitoken` zurueckgegeben und ist danach nicht mehr abrufbar.
// Erzeugen/Widerrufen laufen serverseitig (Cloud Functions `createapitoken` /
// `revokeapitoken`); das Dashboard liest die Liste direkt aus `apiTokens` in
// ZWEI Live-Abos: die Token des aktiven Mandanten (`companyId`) und zusaetzlich
// die globalen Token (`provisioning == true`, im Code auf „ohne companyId"
// eingeengt), zusammengefuehrt und sortiert im Client (T-M31.4, REQ-SEC-035).
// *Bis zum 2026-09-05 stand hier „gefiltert auf den aktiven Mandanten" —
// berichtigt statt geloescht (REQ-OPS-036 Praez. 6).*
//
// Ein GLOBALES TOKEN (REQ-SEC-007 Praez. 3) ist die zweite Klasse: es traegt
// `provisioning: true` und KEINE `companyId` — zwei Merkmale und kein drittes.
// Es darf ALLE SIEBEN Routen der `printerqueueapi` fuer JEDEN Mandanten. Ein
// Token mit `provisioning: true` UND einer `companyId` ist dagegen KEIN globales
// Token: es ist ein Betriebs-Token seines Mandanten, das zusaetzlich Mandanten
// anlegen darf.
//
// ⚠ BERICHTIGT AM 2026-09-05 (T-M31.3, REQ-DM-017 Praez. 1), BERICHTIGT STATT
//   GELOESCHT (REQ-OPS-036 Praez. 6). Bis dahin stand hier: „Ein
//   PROVISIONIERUNGS-TOKEN (REQ-SEC-007) ist der eine Sonderfall: es traegt
//   `provisioning: true` und KEINE `companyId`. Es entsteht administrativ (nicht
//   ueber `createapitoken` — die Funktion kann `provisioning: true` nicht setzen
//   und erzwingt eine `companyId`; seit T-M30.5 verlangt sie zudem die Rolle
//   `admin`, REQ-SEC-031) und erscheint in keiner Token-Liste des Dashboards,
//   weil diese je Mandant filtert."
//
//   ZWEI DINGE DARAN TREFFEN NICHT MEHR ZU: (1) das Token entsteht ab jetzt
//   ueber `createapitoken` mit dem Aufrufparameter `global: true` statt einer
//   `companyId` — genau eines von beidem ist zulaessig, beides und keines sind
//   400 ohne Schreibwirkung (REQ-SEC-035 (b)); (2) die Bezeichnung lautet
//   „globales Token" statt „Provisionierungs-Token" — dieselbe Sache, anderer
//   Name; der FELDNAME `provisioning` bleibt (Umbenennungssperre REQ-OV-008).
//   UNVERAENDERT: das Admin-Gate (REQ-SEC-031). Die Token-Liste filtert seit
//   T-M31.4 (2026-09-05) NICHT mehr nur je Mandant, sondern zeigt globale Token
//   gekennzeichnet dazu und laesst sie widerrufen (REQ-SEC-035) — der Satz
//   „die Bedienstelle traegt T-M31.4" ist damit vollzogen.

export interface ApiToken {
  id:          string;
  /** SHA-256-Hash des Klartext-Tokens (kein Klartext gespeichert). */
  tokenHash:   string;
  /** Nicht-geheimes Praefix (`pq_live_` + 6 Zeichen) zur Anzeige/Identifikation. */
  tokenPrefix: string;
  /**
   * Mandantenzuordnung (REQ-DM-017). OPTIONAL: ein globales Token
   * (REQ-SEC-007 Praez. 3) besteht bewusst ohne Mandanten — das Feld FEHLT dann
   * und ist nicht leer. Betriebs-Token tragen es immer.
   */
  companyId?:  string;
  /**
   * Kennzeichnung im Feld `provisioning` (REQ-SEC-007, REQ-DM-017): nur mit
   * `true` darf ueber `POST /v1/companies` ein Mandant angelegt werden.
   * Fehlendes Feld oder jeder andere Wert = Betriebs-Token (sicherer Standard).
   *
   * ⚠ ZUSAMMEN MIT EINER FEHLENDEN `companyId` bildet sie seit dem 2026-09-04
   *   die Klasse der GLOBALEN TOKEN, und die darf alle sieben Routen — auch die
   *   Drucker-Routen (REQ-SEC-007 Praez. 3 (b)). *Bis dahin stand hier „auf den
   *   Drucker-Routen ohne Wirkung (kein Generalschluessel)"; das galt und gilt
   *   nur noch fuer ein Token, das die Kennzeichnung UND eine `companyId`
   *   traegt — berichtigt statt geloescht, REQ-OPS-036 Praez. 6.*
   */
  provisioning?: boolean;
  /** Frei waehlbares Label. */
  name:        string;
  /** true = gueltig, false = widerrufen. */
  active:      boolean;
  /** Ablaufzeitpunkt; null = unbefristet. */
  expiresAt:   Timestamp | null;
  createdAt:   Timestamp;
  createdBy:   string;
  revokedAt:   Timestamp | null;
  revokedBy:   string;
  /** Zeitpunkt der letzten Verwendung; null = noch nie verwendet. */
  lastUsedAt:  Timestamp | null;
}

// ── Antwort von `createapitoken` (Klartext-Token nur EINMAL) ──────────────────
// ACHTUNG: `token` ist der Klartext und kommt NUR hier einmalig zurueck; er wird
// nirgends persistiert und ist danach nie wieder abrufbar.
export interface CreateApiTokenResult {
  id:          string;
  token:       string;   // Klartext – nur einmalig sichtbar
  tokenPrefix: string;
  /**
   * Der Mandant des Tokens — `null` bei einem GLOBALEN Token
   * (REQ-DM-017 Praez. 1 (d), seit T-M31.3). *Bis zum 2026-09-05 war der Typ
   * `string`, weil `createapitoken` eine `companyId` erzwang; ein leerer
   * Zeichenkettenwert an dieser Stelle waere eine Falschauskunft — ein globales
   * Token hat keinen Mandanten. Berichtigt statt geloescht (REQ-OPS-036
   * Praez. 6).*
   */
  companyId:   string | null;
  name:        string;
  active:      boolean;
  expiresAt:   string | null;
  createdAt:   string;
}

// ── Dashboard helpers ─────────────────────────────────────────────────────────

/** Status badge colour mapping for the UI. */
export const STATUS_COLORS: Record<PrintJobStatus, string> = {
  [PrintJobStatus.PENDING]:   '#f59e0b',   // amber
  [PrintJobStatus.PRINTING]:  '#6366f1',   // indigo (animated)
  [PrintJobStatus.COMPLETED]: '#10b981',   // emerald
  [PrintJobStatus.FAILED]:    '#ef4444',   // red
  [PrintJobStatus.EXPIRED]:   '#94a3b8',   // slate/grey
};

// STATUS_LABELS entfernt – bitte $t('status.pending') etc. via vue-i18n verwenden.
// Die Keys sind in src/i18n/locales/de.ts unter status.* definiert.
